(() => {
  // Install bounds before the initialization guard: a theme preview may already
  // have an older calculator instance, whose inputs must still obey these limits.
  const limitsRevision = 'daily-limits-20261007-3';
  if (window.w21PowerCalculatorLimitsRevision !== limitsRevision) {
    const applyApplianceLimits = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      const card = input.closest('.w21-power-calculator__app :is([data-device-id], [data-catalogue-device-id])');
      if (!card || !['watts', 'hours', 'minutes'].includes(input.dataset.field)) return;
      const hours = card.querySelector('input[data-field="hours"]');
      const minutes = card.querySelector('input[data-field="minutes"]');
      const max = input.dataset.field === 'watts' ? 99999 : input.dataset.field === 'hours' ? 24 : Number(hours?.value) >= 24 ? 0 : 59;
      input.min = '0';
      input.max = String(max);
      input.step = '1';
      if (input.value !== '') {
        const number = Number(input.value);
        const bounded = Number.isFinite(number) ? Math.min(max, Math.max(0, Math.floor(number))) : 0;
        if (number !== bounded) input.value = String(bounded);
      }
      if (hours && minutes) {
        const fullDay = Number(hours.value) >= 24;
        minutes.max = fullDay ? '0' : '59';
        minutes.disabled = fullDay;
        if (fullDay && minutes.value !== '0') {
          minutes.value = '0';
          // Keep any existing calculator instance's energy state in sync.
          minutes.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
      card.dataset.limitsRevision = limitsRevision;
    };
    document.addEventListener('input', applyApplianceLimits, true);
    document.addEventListener('change', applyApplianceLimits, true);
    document.addEventListener('focusin', applyApplianceLimits, true);
    window.w21PowerCalculatorLimitsRevision = limitsRevision;
  }
  if (window.w21PowerCalculatorAssetsLoaded) return;
  window.w21PowerCalculatorAssetsLoaded = true;
  const initializeCalculator = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const locale = ['nl', 'de'].includes(root.dataset.locale) ? root.dataset.locale : 'en';
    // Native number steppers paint an OS background. Keep the number input,
    // bounds and keyboard behaviour, but draw only transparent SVG arrows.
    const syncNumberStepper = box => {
      const input = box.querySelector('input');
      box.querySelectorAll('[data-number-step]').forEach(button => { button.disabled = input.disabled; });
    };
    let numberHold = null;
    const stopNumberHold = event => {
      if (!numberHold || (event?.pointerId !== undefined && event.pointerId !== numberHold.pointerId)) return;
      window.clearTimeout(numberHold.timer);
      numberHold = null;
    };
    window.addEventListener('pointerup', stopNumberHold, true);
    window.addEventListener('pointercancel', stopNumberHold, true);
    window.addEventListener('blur', () => stopNumberHold());
    document.addEventListener('visibilitychange', () => { if (document.hidden) stopNumberHold(); });
    const addNumberStepper = input => {
      const box = input.parentElement;
      if (box.querySelector('[data-number-step]')) return;
      const stepper = document.createElement('span');
      stepper.className = 'w21-power-calculator__number-stepper';
      const actionLabels = { en: ['Increase', 'Decrease'], nl: ['Verhogen', 'Verlagen'], de: ['Erhöhen', 'Verringern'] }[locale];
      [1, -1].forEach((direction, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.numberStep = String(direction);
        button.setAttribute('aria-label', `${actionLabels[index]}: ${input.getAttribute('aria-label') || ''}`);
        button.innerHTML = `<svg viewBox="0 0 12 8" aria-hidden="true"><path d="${direction === 1 ? 'M1 7L6 1l5 6Z' : 'M1 1l5 6 5-6Z'}"/></svg>`;
        let pointerStepped = false;
        const stepValue = () => {
          if (!input.isConnected || !button.isConnected || input.disabled || input.readOnly || button.disabled) return false;
          const previous = input.value;
          input.stepUp(direction);
          if (input.value === previous) return false;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        };
        button.addEventListener('pointerdown', event => {
          event.preventDefault();
          if (event.button !== 0 || event.isPrimary === false ||
            !input.closest('[data-device-id], [data-catalogue-device-id]')) return;
          event.stopPropagation();
          stopNumberHold();
          pointerStepped = true;
          if (!stepValue()) return;
          const hold = { button, pointerId: event.pointerId, timer: null };
          numberHold = hold;
          const repeat = () => {
            if (numberHold !== hold) return;
            if (!root.isConnected || !stepValue()) { stopNumberHold(); return; }
            hold.timer = window.setTimeout(repeat, 90);
          };
          hold.timer = window.setTimeout(repeat, 350);
        });
        button.addEventListener('pointerleave', event => {
          if (numberHold?.button === button) stopNumberHold(event);
        });
        button.addEventListener('contextmenu', event => {
          if (input.closest('[data-device-id], [data-catalogue-device-id]')) event.preventDefault();
        });
        button.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          // Pointer-down already supplies the first step. Keyboard activation
          // remains a single step; releasing a held pointer never adds an extra.
          if (event.detail > 0 && pointerStepped) { pointerStepped = false; return; }
          pointerStepped = false;
          stepValue();
        });
        stepper.appendChild(button);
      });
      box.appendChild(stepper);
      syncNumberStepper(box);
    };
    root.querySelectorAll('.w21-power-calculator__solar-hours-field > input[type="number"]:not([readonly])').forEach(addNumberStepper);
    root.addEventListener('input', event => {
      if (!(event.target instanceof HTMLInputElement)) return;
      const group = event.target.closest('[data-device-id], [data-catalogue-device-id]') || event.target.parentElement;
      group.querySelectorAll('.w21-power-calculator__solar-hours-field').forEach(syncNumberStepper);
    });
    const translations = {
      en: {
        introEyebrow: 'Configure and calculate', introTitle: 'Let’s build your setup', introText: 'We help you create a personalised setup based on your appliances, daily usage and expected solar input.', benefitsLabel: 'Calculator benefits', benefitQuick: 'Quick personalised result', benefitSolar: 'Practical solar estimate', benefitSimple: 'No technical knowledge needed', calculateSetup: 'Calculate my power setup', closeCalculator: 'Close power calculator', setup: 'Setup', appliances: 'Appliances', result: 'Result', step1: 'Step 1', step2: 'Step 2', step3: 'Step 3', mixText: "Choose your power station and optional solar panels. Build the setup that suits you.", addPowerStation: 'Add power station', switchPowerStation: 'Switch power station', powerStation: 'Power station', threeOptions: '3 options', addExpansionBattery: 'Add expansion battery', addSolarPanels: 'Add solar panel(s)', switchSolarPanels: 'Switch solar panel(s)', hideSolarPanels: 'Hide solar panel(s)', solarPanel: 'Solar panel', choose: 'Choose', sunlightProfile: 'Sunlight Profile', northEurope: 'North Europe', southEurope: 'South Europe', custom: 'Custom', next: 'Next', back: 'Back', scenarioTitle: 'Add your appliances', scenarioText: 'Add your appliances. Start with our suggested daily usage. Adjust the wattage and usage for your setup. Can’t find what you need? Add a custom appliance.', simultaneousTitle: 'Select simultaneous use', simultaneousText: 'Select the appliances that may run at the same time. This calculates peak power.', estimateShort: 'This calculation is an estimate.', reset: 'Reset', openResults: 'Open results', closeResults: 'Close results', disclaimer: '<span class="w21-power-calculator__disclaimer-intro">This calculation is an estimate. </span>Actual runtime of your power station may vary depending on temperature, battery age, inverter efficiency, device usage, and peak power demand. Expected solar input includes an estimated 20% allowance for real-world losses. Source: <span class="w21-power-calculator__solar-source">European Commission PVGIS</span> (June–July averages).',
        selectSetup: 'Select setup', outputEmpty: 'Select a power station to see available output.', capacityEmpty: 'Select a setup to see available capacity.', outputLeft: 'left', capacityLeft: 'left', outputExceeded: 'Output exceeded by', capacityShort: 'Capacity short by', dailyUse: 'Daily use', solarInput: 'Solar input', netDailyNeed: 'Daily energy need', batteryCapacity: 'Battery capacity', solarCapacity: 'Solar capacity', peakPower: 'Peak power', hours: 'Hours', minutes: 'Minutes', chargesPerDay: 'Charges per day', charge: 'charge', watts: 'Watt', addCustomDevice: 'Add custom appliance', selectedAppliances: 'Selected appliances', selectedAppliancesHelp: 'Adjust wattage and daily use.', selectedEmpty: 'No appliances selected yet.', customDeviceName: 'Custom Appliance', compatible: 'Compatible', notCompatible: 'Not compatible with selected power station', selected: 'selected', capacity: 'Capacity', output: 'Output', extraCapacity: 'Extra capacity', estDailyInput: 'Solar input', barcode: 'EAN', outputLimitSelectStation: 'Select a power station to set output limits.', customOutputMax: 'Custom appliance max output', simultaneousCurrent: 'Current simultaneous output', simultaneousLimitReached: 'This would exceed the selected power station output.', dailyGroup: 'Add daily essentials', dailyGroupHelp: 'Everyday appliances up to 100W', comfortGroup: 'Add comfort essentials', comfortGroupHelp: 'Comfort appliances up to 1200W', highGroup: 'Add high-power essentials', highGroupHelp: 'High-power appliances above 1200W', backpackTraveler: 'Back-Pack Traveler', comfortVanlife: 'Comfort Vanlife', offGridTraveler: 'Off-Grid Traveler', customSetup: 'Custom Setup'
      },
      nl: {
        introEyebrow: 'Configureer en bereken', introTitle: 'Stel jouw setup samen', introText: 'We helpen je een persoonlijke setup samen te stellen op basis van je apparaten, dagelijks gebruik en verwachte zonne-opbrengst.', benefitsLabel: 'Voordelen van de calculator', benefitQuick: 'Snel persoonlijk resultaat', benefitSolar: 'Praktische schatting van zonne-energie', benefitSimple: 'Geen technische kennis nodig', calculateSetup: 'Bereken mijn stroomsetup', closeCalculator: 'Sluit stroomcalculator', setup: 'Setup', appliances: 'Apparaten', result: 'Resultaat', step1: 'Stap 1', step2: 'Stap 2', step3: 'Stap 3', mixText: 'Kies je powerstation en optionele zonnepanelen. Stel de setup samen die bij jou past.', addPowerStation: 'Voeg powerstation toe', switchPowerStation: 'Wissel powerstation', powerStation: 'Powerstation', threeOptions: '3 opties', addExpansionBattery: 'Voeg uitbreidingsaccu toe', addSolarPanels: 'Voeg zonnepaneel(en) toe', switchSolarPanels: 'Wissel zonnepaneel(en)', hideSolarPanels: 'Verberg zonnepanelen', solarPanel: 'Zonnepaneel', choose: 'Kies', sunlightProfile: 'Zonlichtprofiel', northEurope: 'Noord-Europa', southEurope: 'Zuid-Europa', custom: 'Aangepast', next: 'Volgende', back: 'Terug', scenarioTitle: 'Voeg je apparaten toe', scenarioText: 'Voeg je apparaten toe. Begin met ons voorgestelde dagelijks gebruik. Pas het wattage en gebruik aan voor jouw setup. Kun je jouw apparaat niet vinden? Voeg een eigen apparaat toe.', simultaneousTitle: 'Selecteer gelijktijdig gebruik', simultaneousText: 'Selecteer de apparaten die tegelijkertijd kunnen werken. Hiermee berekenen we het piekvermogen.', estimateShort: 'Deze berekening is een schatting.', reset: 'Reset', openResults: 'Open resultaten', closeResults: 'Sluit resultaten', disclaimer: '<span class="w21-power-calculator__disclaimer-intro">Deze berekening is een schatting. </span>De werkelijke gebruiksduur van je powerstation kan variëren door temperatuur, leeftijd van de accu, efficiëntie van de omvormer, apparaatgebruik en piekvermogen. Bij de verwachte zonne-opbrengst is rekening gehouden met circa 20% verlies in de praktijk. Bron: <span class="w21-power-calculator__solar-source">Europese Commissie PVGIS</span> (gemiddelden juni–juli).',
        selectSetup: 'Selecteer een setup', outputEmpty: 'Selecteer een powerstation om het beschikbare vermogen te zien.', capacityEmpty: 'Selecteer een setup om de beschikbare capaciteit te zien.', outputLeft: 'over', capacityLeft: 'over', outputExceeded: 'Vermogen overschreden met', capacityShort: 'Capaciteit tekort met', dailyUse: 'Dagelijks verbruik', solarInput: 'Zonne-opbrengst', netDailyNeed: 'Dagelijkse energiebehoefte', batteryCapacity: 'Accucapaciteit', solarCapacity: 'Zonnecapaciteit', peakPower: 'Piekvermogen', hours: 'Uren', minutes: 'Minuten', chargesPerDay: 'Laadbeurten per dag', charge: 'laadbeurt', watts: 'Watt', addCustomDevice: 'Voeg eigen apparaat toe', selectedAppliances: 'Geselecteerde apparaten', selectedAppliancesHelp: 'Pas het wattage en dagelijks gebruik aan.', selectedEmpty: 'Nog geen apparaten geselecteerd.', customDeviceName: 'Eigen apparaat', compatible: 'Compatibel', notCompatible: 'Niet compatibel met het geselecteerde powerstation', selected: 'geselecteerd', capacity: 'Capaciteit', output: 'Vermogen', extraCapacity: 'Extra capaciteit', estDailyInput: 'Zonne-opbrengst', barcode: 'EAN', outputLimitSelectStation: 'Selecteer een powerstation om vermogenslimieten in te stellen.', customOutputMax: 'Maximaal vermogen eigen apparaat', simultaneousCurrent: 'Huidig gelijktijdig vermogen', simultaneousLimitReached: 'Dit overschrijdt het vermogen van het geselecteerde powerstation.', dailyGroup: 'Voeg dagelijkse apparaten toe', dailyGroupHelp: 'Dagelijkse apparaten tot 100W', comfortGroup: 'Voeg comfortapparaten toe', comfortGroupHelp: 'Comfortapparaten tot 1200W', highGroup: 'Voeg apparaten met hoog vermogen toe', highGroupHelp: 'Apparaten boven 1200W', backpackTraveler: 'Backpackreiziger', comfortVanlife: 'Comfort Vanlife', offGridTraveler: 'Off-grid reiziger', customSetup: 'Aangepaste setup'
      },
      de: {
        introEyebrow: 'Konfigurieren und berechnen', introTitle: 'Stelle dein Setup zusammen', introText: 'Wir helfen dir, ein persönliches Setup anhand deiner Geräte, der täglichen Nutzung und des erwarteten Solarertrags zusammenzustellen.', benefitsLabel: 'Vorteile des Rechners', benefitQuick: 'Schnelles persönliches Ergebnis', benefitSolar: 'Praxisnahe Solarschätzung', benefitSimple: 'Keine technischen Kenntnisse erforderlich', calculateSetup: 'Mein Strom-Setup berechnen', closeCalculator: 'Stromrechner schließen', setup: 'Setup', appliances: 'Geräte', result: 'Ergebnis', step1: 'Schritt 1', step2: 'Schritt 2', step3: 'Schritt 3', mixText: 'Wähle deine Powerstation und optionale Solarmodule. Stelle das Setup zusammen, das zu dir passt.', addPowerStation: 'Powerstation hinzufügen', switchPowerStation: 'Powerstation wechseln', powerStation: 'Powerstation', threeOptions: '3 Optionen', addExpansionBattery: 'Zusatzakku hinzufügen', addSolarPanels: 'Solarmodul(e) hinzufügen', switchSolarPanels: 'Solarmodul(e) wechseln', hideSolarPanels: 'Solarmodule ausblenden', solarPanel: 'Solarmodul', choose: 'Auswählen', sunlightProfile: 'Sonnenlichtprofil', northEurope: 'Nordeuropa', southEurope: 'Südeuropa', custom: 'Benutzerdefiniert', next: 'Weiter', back: 'Zurück', scenarioTitle: 'Füge deine Geräte hinzu', scenarioText: 'Füge deine Geräte hinzu. Beginne mit unserer empfohlenen täglichen Nutzung. Passe Leistung und Nutzung an dein Setup an. Findest du dein Gerät nicht? Füge ein eigenes Gerät hinzu.', simultaneousTitle: 'Gleichzeitige Nutzung auswählen', simultaneousText: 'Wähle die Geräte aus, die gleichzeitig laufen können. Daraus berechnen wir die Spitzenleistung.', estimateShort: 'Diese Berechnung ist eine Schätzung.', reset: 'Zurücksetzen', openResults: 'Ergebnisse öffnen', closeResults: 'Ergebnisse schließen', disclaimer: '<span class="w21-power-calculator__disclaimer-intro">Diese Berechnung ist eine Schätzung. </span>Die tatsächliche Laufzeit deiner Powerstation kann je nach Temperatur, Akkualter, Wechselrichtereffizienz, Gerätenutzung und Spitzenleistung variieren. Beim erwarteten Solarertrag sind etwa 20% Praxisverluste berücksichtigt. Quelle: <span class="w21-power-calculator__solar-source">Europäische Kommission PVGIS</span> (Durchschnitt Juni–Juli).',
        selectSetup: 'Setup auswählen', outputEmpty: 'Wähle eine Powerstation, um die verfügbare Leistung zu sehen.', capacityEmpty: 'Wähle ein Setup, um die verfügbare Kapazität zu sehen.', outputLeft: 'übrig', capacityLeft: 'übrig', outputExceeded: 'Leistung überschritten um', capacityShort: 'Kapazität unterschritten um', dailyUse: 'Täglicher Verbrauch', solarInput: 'Solarertrag', netDailyNeed: 'Täglicher Energiebedarf', batteryCapacity: 'Akkukapazität', solarCapacity: 'Solarkapazität', peakPower: 'Spitzenleistung', hours: 'Stunden', minutes: 'Minuten', chargesPerDay: 'Ladevorgänge pro Tag', charge: 'Ladevorgang', watts: 'Watt', addCustomDevice: 'Eigenes Gerät hinzufügen', selectedAppliances: 'Ausgewählte Geräte', selectedAppliancesHelp: 'Leistung und tägliche Nutzung anpassen.', selectedEmpty: 'Noch keine Geräte ausgewählt.', customDeviceName: 'Eigenes Gerät', compatible: 'Kompatibel', notCompatible: 'Nicht mit der ausgewählten Powerstation kompatibel', selected: 'ausgewählt', capacity: 'Kapazität', output: 'Leistung', extraCapacity: 'Zusätzliche Kapazität', estDailyInput: 'Solarertrag', barcode: 'EAN', outputLimitSelectStation: 'Wähle eine Powerstation, um Leistungsgrenzen festzulegen.', customOutputMax: 'Maximale Leistung des eigenen Geräts', simultaneousCurrent: 'Aktuelle gleichzeitige Leistung', simultaneousLimitReached: 'Dies überschreitet die Leistung der ausgewählten Powerstation.', dailyGroup: 'Alltagsgeräte hinzufügen', dailyGroupHelp: 'Alltagsgeräte bis 100W', comfortGroup: 'Komfortgeräte hinzufügen', comfortGroupHelp: 'Komfortgeräte bis 1200W', highGroup: 'Leistungsstarke Geräte hinzufügen', highGroupHelp: 'Geräte über 1200W', backpackTraveler: 'Rucksackreisender', comfortVanlife: 'Komfort Vanlife', offGridTraveler: 'Off-grid Reisender', customSetup: 'Benutzerdefiniertes Setup'
      }
    };
    const labels = { ...translations.en, ...translations[locale] };
    Object.assign(labels, {
      en: { calculateSetup: 'Start calculation', closeCalculator: 'Close calculation' },
      nl: { calculateSetup: 'Start berekening', closeCalculator: 'Sluit berekening' },
      de: { calculateSetup: 'Berechnung starten', closeCalculator: 'Berechnung schließen' }
    }[locale]);
    Object.assign(labels, {
      en: { step5: 'Step 5', recommendedTitle: 'Result', recommendedText: 'Compare your selection with our recommended setup. Find the combination that matches your energy needs.', yourSelection: 'Your selection', recommendationEmpty: 'Add appliances in step 1 to see a recommendation.', selectionEmpty: 'Select a power station in step 3.', recommendationUnavailable: 'No available setup covers these energy and power requirements.', recommendationBasis: 'Based on one day of battery capacity and your sunlight profile.', solarShortfall: 'The compatible solar panels cannot fully cover your daily energy use. Additional charging is needed.' },
      nl: { step5: 'Stap 5', recommendedTitle: 'Resultaat', recommendedText: 'Vergelijk je selectie met onze aanbevolen setup. Vind de combinatie die aansluit op jouw energiebehoefte.', yourSelection: 'Jouw selectie', recommendationEmpty: 'Voeg apparaten toe in stap 1 om een aanbeveling te zien.', selectionEmpty: 'Selecteer een powerstation in stap 3.', recommendationUnavailable: 'Geen beschikbare setup dekt deze energie- en vermogensbehoefte.', recommendationBasis: 'Gebaseerd op één dag accucapaciteit en jouw zonlichtprofiel.', solarShortfall: 'De compatibele zonnepanelen dekken je dagelijkse energieverbruik niet volledig. Extra opladen is nodig.' },
      de: { step5: 'Schritt 5', recommendedTitle: 'Ergebnis', recommendedText: 'Vergleiche deine Auswahl mit unserem empfohlenen Setup. Finde die Kombination für deinen Energiebedarf.', yourSelection: 'Deine Auswahl', recommendationEmpty: 'Füge Geräte in Schritt 1 hinzu um eine Empfehlung zu sehen.', selectionEmpty: 'Wähle eine Powerstation in Schritt 3.', recommendationUnavailable: 'Kein verfügbares Setup deckt diesen Energie- und Leistungsbedarf.', recommendationBasis: 'Basierend auf Akkukapazität für einen Tag und deinem Sonnenlichtprofil.', solarShortfall: 'Die kompatiblen Solarmodule decken deinen täglichen Energiebedarf nicht vollständig. Zusätzliches Laden ist erforderlich.' }
    }[locale]);
    labels.recommendationBasis = {
      en: 'Based on one day of energy use with at least 20% battery reserve. Optional solar charging uses your sunlight profile.',
      nl: 'Gebaseerd op één dag energieverbruik met minimaal 20% accureserve. Optioneel opladen via de zon gebruikt jouw zonlichtprofiel.',
      de: 'Basierend auf dem Energiebedarf für einen Tag mit mindestens 20% Akkureserve. Optionales Solarladen berücksichtigt dein Sonnenlichtprofil.'
    }[locale];
    Object.assign(labels, {
      en: { enableSolarPanels: 'Enable solar panel(s)', disableSolarPanels: 'Disable solar panel(s)' },
      nl: { enableSolarPanels: 'Zonnepanelen inschakelen', disableSolarPanels: 'Zonnepanelen uitschakelen' },
      de: { enableSolarPanels: 'Solarmodule aktivieren', disableSolarPanels: 'Solarmodule deaktivieren' }
    }[locale]);
    labels.solarAdvice = labels.disableSolarPanels;
    labels.daysUnit = { en: 'days', nl: 'dagen', de: 'Tage' }[locale];
    Object.assign(labels, {
      en: { daysAway: 'Days away', tripAssumptions: 'Start with a full battery. No charging from the mains or your vehicle is included.', recommendationBasis: 'Based on your trip duration with at least 20% battery reserve at the end of each day. Optional solar charging uses your sunlight profile.' },
      nl: { daysAway: 'Aantal dagen onderweg', tripAssumptions: 'Je vertrekt met een volle accu. Opladen via netstroom of je voertuig telt niet mee.', recommendationBasis: 'Gebaseerd op je reisduur met minimaal 20% accureserve aan het einde van elke dag. Optioneel opladen via de zon gebruikt jouw zonlichtprofiel.' },
      de: { daysAway: 'Tage unterwegs', tripAssumptions: 'Du startest mit einem vollen Akku. Laden über das Stromnetz oder dein Fahrzeug wird nicht berücksichtigt.', recommendationBasis: 'Basierend auf deiner Reisedauer mit mindestens 20% Akkureserve am Ende jedes Tages. Optionales Solarladen berücksichtigt dein Sonnenlichtprofil.' }
    }[locale]);
    labels.recommendationBasis = {
      en: 'Normal advice keeps 20% of the main unit capacity in reserve at the end of each day. Expansion batteries and solar input do not increase this reserve. If no setup meets it, only the maximum available setup is assessed using full battery capacity.',
      nl: 'Normaal advies houdt aan het einde van elke dag 20% van de hoofdunitcapaciteit als reserve. Uitbreidingsaccu’s en zonne-opbrengst verhogen die reserve niet. Past geen enkele setup daarmee, dan rekenen we alleen bij de maximaal beschikbare setup met de volledige accucapaciteit.',
      de: 'Normale Empfehlungen halten am Ende jedes Tages 20% der Kapazität der Haupteinheit als Reserve. Zusatzakkus und Solarertrag erhöhen diese Reserve nicht. Reicht kein Setup damit aus, wird nur das maximal verfügbare Setup mit der vollen Akkukapazität bewertet.'
    }[locale];
    labels.selectedAppliancesText = {
      en: "These are the appliances you've selected. This calculates your daily energy consumption.",
      nl: 'Dit zijn de apparaten die je hebt geselecteerd. Hiermee berekenen we je dagelijkse energieverbruik.',
      de: 'Dies sind deine ausgewählten Geräte. Damit berechnen wir deinen täglichen Energieverbrauch.'
    }[locale];
    Object.assign(labels, {
      en: { simultaneousTitle: 'Calculate peak power', emptyAppliance: 'Add appliances step 1', step4: 'Step 3', dashboardTitle: 'Live calculation dashboard', dashboardText: 'Explore your results. See how your setup matches your needs. Adjust your setup to see what changes.' },
      nl: { simultaneousTitle: 'Bereken het piekvermogen', emptyAppliance: 'Voeg apparaten toe in stap 1', step4: 'Stap 3', dashboardTitle: 'Live berekeningsoverzicht', dashboardText: 'Bekijk je resultaten. Zie hoe jouw setup aansluit op je behoeften. Pas je setup aan om te zien wat er verandert.' },
      de: { simultaneousTitle: 'Spitzenleistung berechnen', emptyAppliance: 'Geräte in Schritt 1 hinzufügen', step4: 'Schritt 3', dashboardTitle: 'Live-Berechnungsübersicht', dashboardText: 'Entdecke deine Ergebnisse. Sieh wie dein Setup zu deinen Bedürfnissen passt. Passe dein Setup an und sieh was sich verändert.' }
    }[locale]);
    const applicationCategoryLabels = {
      en: { household: 'Household Appliances', pro: 'Pro (Tools) Appliances', custom: 'Custom Appliances', proEmpty: 'No Pro (Tools) appliances available yet.' },
      nl: { household: 'Huishoudelijke apparaten', pro: 'Pro-apparaten (gereedschap)', custom: 'Eigen apparaten', proEmpty: 'Er zijn nog geen Pro-apparaten (gereedschap) beschikbaar.' },
      de: { household: 'Haushaltsgeräte', pro: 'Pro-Geräte (Werkzeuge)', custom: 'Eigene Geräte', proEmpty: 'Noch keine Pro-Geräte (Werkzeuge) verfügbar.' }
    }[locale];

    labels.step4 = { en: 'Step 2', nl: 'Stap 2', de: 'Schritt 2' }[locale];
    root.closest('.w21-power-calculator')?.querySelectorAll('[data-i18n]').forEach((element) => {
      const translation = labels[element.dataset.i18n];
      if (translation) element.textContent = translation;
    });
    root.closest('.w21-power-calculator')?.querySelectorAll('[data-i18n-html]').forEach((element) => {
      const translation = labels[element.dataset.i18nHtml];
      if (translation) element.innerHTML = translation;
    });
    root.closest('.w21-power-calculator')?.querySelectorAll('[data-i18n-aria-label]').forEach((element) => {
      const translation = labels[element.dataset.i18nAriaLabel];
      if (translation) element.setAttribute('aria-label', translation);
    });

    Object.assign(labels, {
      selectSetup: 'Select setup',
      outputEmpty: 'Select a power station to see available output.',
      capacityEmpty: 'Select a setup to see available capacity.',
      outputLeft: 'left',
      capacityLeft: 'left',
      outputExceeded: 'Output exceeded by',
      capacityShort: 'Capacity short by',
      dailyUse: 'Daily use',
      solarInput: 'Solar input',
      netDailyNeed: 'Daily energy need',
      batteryCapacity: 'Battery capacity',
      solarCapacity: 'Solar capacity',
      peakPower: 'Peak power',
      hours: 'Hours',
      minutes: 'Minutes',
      chargesPerDay: 'Charges per day',
      charge: 'charge',
      watts: 'Watt',
      addCustomDevice: 'Add custom appliance',
      selectedAppliances: 'Selected appliances',
      selectedAppliancesHelp: 'Adjust wattage and daily use.',
      selectedEmpty: 'No appliances selected yet.',
      customDeviceName: 'Custom Appliance',
      compatible: 'Compatible',
      notCompatible: 'Not compatible with selected power station',
      selected: 'selected',
      capacity: 'Capacity',
      output: 'Output',
      extraCapacity: 'Extra capacity',
      estDailyInput: 'Solar input',
      barcode: 'EAN',
      outputLimitSelectStation: 'Select a power station to set output limits.',
      customOutputMax: 'Custom appliance max output',
      simultaneousCurrent: 'Current simultaneous output',
      simultaneousLimitReached: 'This would exceed the selected power station output.'
    }, translations[locale]);
    Object.assign(labels, {
      change: locale === 'nl' ? 'Wijzigen' : locale === 'de' ? 'Ändern' : 'Change',
      compatibleCount: locale === 'nl' ? 'compatibel' : locale === 'de' ? 'kompatibel' : 'compatible',
      selectPowerFirst: locale === 'nl' ? 'Kies eerst een powerstation' : locale === 'de' ? 'Zuerst Powerstation wählen' : 'Select power first',
      decreaseExpansion: locale === 'nl' ? 'Verminder uitbreidingsaccu’s' : locale === 'de' ? 'Zusatzakkus verringern' : 'Decrease expansion batteries',
      increaseExpansion: locale === 'nl' ? 'Voeg uitbreidingsaccu toe' : locale === 'de' ? 'Zusatzakku hinzufügen' : 'Increase expansion batteries',
      selectRegion: locale === 'nl' ? 'Selecteer' : locale === 'de' ? 'Auswählen' : 'Select'
      ,practicalSolarInput: locale === 'nl' ? 'Praktische zonne-opbrengst' : locale === 'de' ? 'Praktischer Solarertrag' : 'Practical solar input'
      ,expectedSolarInput: locale === 'nl' ? 'Verwachte zonne-opbrengst' : locale === 'de' ? 'Erwarteter Solarertrag' : 'Expected solar input'
      ,shortCapacity: locale === 'nl' ? 'Capaciteit' : locale === 'de' ? 'Kapazität' : 'Capacity'
      ,shortSolar: locale === 'nl' ? 'Zon' : locale === 'de' ? 'Solar' : 'Solar'
      ,shortConsumption: locale === 'nl' ? 'Verbruik' : locale === 'de' ? 'Verbrauch' : 'Consumption'
      ,decreaseSolar: locale === 'nl' ? 'Verminder zonnepanelen' : locale === 'de' ? 'Solarmodule verringern' : 'Decrease solar panels'
      ,increaseSolar: locale === 'nl' ? 'Voeg zonnepaneel toe' : locale === 'de' ? 'Solarmodul hinzufügen' : 'Increase solar panels'
      ,removeFromSimultaneous: locale === 'nl' ? 'Verwijder uit gelijktijdig gebruik' : locale === 'de' ? 'Aus gleichzeitiger Nutzung entfernen' : 'Remove from simultaneous use'
      ,includeInSimultaneous: locale === 'nl' ? 'Voeg toe aan gelijktijdig gebruik' : locale === 'de' ? 'Zur gleichzeitigen Nutzung hinzufügen' : 'Include in simultaneous use'
      ,excludeFromSimultaneous: locale === 'nl' ? 'Sluit uit van gelijktijdig gebruik' : locale === 'de' ? 'Von gleichzeitiger Nutzung ausschließen' : 'Exclude from simultaneous use'
    });
    Object.assign(labels, {
      en: { output: 'Power', capacity: 'Battery', extraCapacity: 'Battery', shortCapacity: 'Battery', peakPower: 'Power', netDailyNeed: 'Consumption', practicalSolarInput: 'Solar Input', batteryCapacity: 'Battery', solarInput: 'Solar Input', shortSolar: 'Solar Input' },
      nl: { output: 'Vermogen', capacity: 'Accu', extraCapacity: 'Accu', shortCapacity: 'Accu', peakPower: 'Vermogen', netDailyNeed: 'Verbruik', practicalSolarInput: 'Zonne-opbrengst', batteryCapacity: 'Accu', solarInput: 'Zonne-opbrengst', shortSolar: 'Zonne-opbrengst' },
      de: { output: 'Leistung', capacity: 'Akku', extraCapacity: 'Akku', shortCapacity: 'Akku', peakPower: 'Leistung', netDailyNeed: 'Verbrauch', practicalSolarInput: 'Solarertrag', batteryCapacity: 'Akku', solarInput: 'Solarertrag', shortSolar: 'Solarertrag' }
    }[locale]);
    const audienceNames = { rv_weekend_camper: labels.backpackTraveler, rv_comfort_vanlife: labels.comfortVanlife, rv_off_grid_camper: labels.offGridTraveler };
    const plusIcon = '<svg class="w21-power-calculator__plus-symbol" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M10 4v12M4 10h12"/></svg>';
    const minusIcon = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4 10h12"/></svg>';
    const checkIcon = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="m4.5 10 3.5 3.5 7.5-8"/></svg>';
    const configurationElement = document.getElementById('w21-calculator-config-' + root.dataset.sectionId);
    let configuredProducts = {};
    try {
      configuredProducts = JSON.parse(configurationElement?.textContent || '{}');
    } catch (error) {
      console.warn('Power calculator product configuration could not be read.', error);
    }
    const productReferences = Object.fromEntries([
      ['move_800', 'EveBase Move 800 Power Station'],
      ['move_1800', 'EveBase Move 1800 Power Station'],
      ['move_2000', 'EveBase Move 2000 Power Station'],
      ['expansion', 'Move 1800 Expansion Battery'],
      ['f120', 'Flexsolar F120 Portable Solar Panel'],
      ['f240', 'Flexsolar F240 Portable Solar Panel'],
      ['f360', 'Flexsolar F360 Portable Solar Panel']
    ].map(([id, title]) => [id, { title, url: '', image: '', barcode: '', ...configuredProducts[id] }]));
    const audiences = [
      { id: 'rv_weekend_camper', name: 'Back-Pack Traveler' },
      { id: 'rv_comfort_vanlife', name: 'Comfort Vanlife' },
      { id: 'rv_off_grid_camper', name: 'Off-Grid Traveler' }
    ];

    const scenarioSelections = {
      rv_weekend_camper: ['led_lighting', 'phone', 'music_speaker', 'camera'],
      rv_comfort_vanlife: ['led_lighting', 'phone', 'music_speaker', 'laptop', 'fan', 'coffee_machine_camping'],
      rv_off_grid_camper: ['led_lighting', 'phone', 'music_speaker', 'laptop', 'fan', 'coffee_machine_camping', 'mini_electric_kettle', 'electric_grill']
    };

    const powerStations = [
      { id: 'move_800', segmentName: 'Move 800', output: 800, capacity: 512, compatibleSolar: ['f120'], compatibleExpansion: false },
      { id: 'move_1800', segmentName: 'Move 1800', output: 1800, capacity: 1536, compatibleSolar: ['f120', 'f240', 'f360'], compatibleExpansion: true },
      { id: 'move_2000', segmentName: 'Move 2000', output: 2000, capacity: 2048, compatibleSolar: ['f120', 'f240', 'f360'], compatibleExpansion: false }
    ].map((item) => ({ ...item, ...productReferences[item.id], name: productReferences[item.id].title, segmentName: item.segmentName }));

    const expansionBattery = { id: 'expansion', ...productReferences.expansion, name: productReferences.expansion.title, segmentName: 'Move 1800 Exp', capacity: 1536, maxQuantity: 2 };
    const solarPanels = [
      { id: 'f120', segmentName: 'F120', watts: 120 },
      { id: 'f240', segmentName: 'F240', watts: 240 },
      { id: 'f360', segmentName: 'F360', watts: 360 }
    ].map((item) => ({ ...item, ...productReferences[item.id], name: productReferences[item.id].title, segmentName: item.segmentName }));

    const solarMaxByStation = {
      move_800: { f120: 1, f240: 0, f360: 0 },
      move_1800: { f120: 3, f240: 2, f360: 1 },
      move_2000: { f120: 3, f240: 2, f360: 1 }
    };

    // Energy defaults are defined in the shared appliance catalogue below.

    const openDeviceGroups = new Set(['household']);
    let applianceSearch = '';
    let mobileCatalogueOpen = false;
    let draggedApplianceId = '';
    let catalogueScrollTop = 0;
    let selectionScrollTop = 0;
    let catalogueResizeObserver = null;
    let catalogueAnimationFrame = 0;
    let catalogueLayoutRefresh = () => {};
    let applianceDragCleanup = null;
    const openOnlyDeviceGroup = (groupId) => {
      openDeviceGroups.clear();
      if (groupId) openDeviceGroups.add(groupId);
    };

    const solarRegions = {
      north: { practicalSunHours: 4.0 },
      south: { practicalSunHours: 5.5 },
      custom: { practicalSunHours: 7.0 }
    };
    const state = { step: 1, audienceId: '', selectedPowerStationId: '', expansionCount: 0, selectedSolarId: '', solarCount: 0, devices: [], customCount: 0, selectionSequence: 0, solarRegion: 'north', solarSunHours: solarRegions.north.practicalSunHours };
    let rememberedSolarSelection = null;
    // Advice is independent of the customer's products. It updates Mix & Match
    // until a manual product/count change, and reconnects only via Switch.
    let selectionIsManual = false;
    let currentRecommendation = null;
    const rememberSolarSelection = () => {
      if (state.selectedSolarId && state.solarCount > 0) {
        rememberedSolarSelection = { id: state.selectedSolarId, count: state.solarCount };
      }
    };
    const getSetupSelection = () => ({
      selectedPowerStationId: state.selectedPowerStationId,
      expansionCount: state.expansionCount,
      selectedSolarId: state.selectedSolarId,
      solarCount: state.solarCount
    });
    const sameSetup = (a, b) => ['selectedPowerStationId', 'expansionCount', 'selectedSolarId', 'solarCount'].every((key) => a[key] === b[key]);
    const getRecommendedSelection = choice => ({
      selectedPowerStationId: choice.station.id,
      expansionCount: choice.expansionCount,
      selectedSolarId: choice.solar?.panel.id || '',
      solarCount: choice.solar?.quantity || 0
    });
    const markCustomScenario = () => { state.audienceId = 'custom'; };
    Object.assign(labels, {
      en: { switchSuggested: 'Switch to suggested setup' },
      nl: { switchSuggested: 'Schakel over naar de voorgestelde setup' },
      de: { switchSuggested: 'Zum vorgeschlagenen Setup wechseln' }
    }[locale]);

    const stepButtons = Array.from(root.querySelectorAll('[data-step-button]'));
    const panels = Array.from(root.querySelectorAll('[data-step-panel]'));
    const audienceGrid = root.querySelector('[data-audience-grid]');
    const powerProductGrid = root.querySelector('[data-power-product-grid]');
    const solarProductGrid = root.querySelector('[data-solar-product-grid]');
    // Persistent copies share state, not DOM nodes or IDs. Recalculation must
    // not replace a focused sunlight input or a range-slider thumb.
    const sunlightTemplate = root.querySelector('.w21-power-calculator__solar-region');
    root.querySelectorAll('[data-result-settings]').forEach((settings, index) => {
      if (!sunlightTemplate) return;
      const profile = sunlightTemplate.cloneNode(true);
      profile.removeAttribute('data-mix-sunlight');
      profile.setAttribute('data-results-sunlight', '');
      profile.hidden = true;
      const ids = new Map();
      profile.querySelectorAll('[id]').forEach(el => { const previous = el.id; el.id += '-results-' + index; ids.set(previous, el.id); });
      profile.querySelectorAll('[aria-controls], [aria-labelledby], [for]').forEach(el => {
        ['aria-controls', 'aria-labelledby', 'for'].forEach(name => { if (el.hasAttribute(name)) el.setAttribute(name, el.getAttribute(name).split(' ').map(id => ids.get(id) || id).join(' ')); });
      });
      profile.querySelectorAll('.w21-power-calculator__number-stepper').forEach(el => el.remove());
      settings.appendChild(profile);
      profile.querySelectorAll('input[type="number"]').forEach(addNumberStepper);
    });
    const solarRegionButtons = Array.from(root.querySelectorAll('[data-solar-region]'));
    const customSolarHoursInputs = Array.from(root.querySelectorAll('[data-custom-solar-hours]'));
    const deviceGrid = root.querySelector('[data-device-grid]');
    const results = root.querySelector('[data-results]');
    const resultMetrics = [results, root.querySelector('[data-step-results]')].filter(Boolean);
    const stationOutput = root.querySelector('[data-station-output]');
    const appliancesOutput = root.querySelector('[data-appliances-output]');
    const outputStatus = root.querySelector('[data-output-status]');
    const capacityTotal = root.querySelector('[data-capacity-total]');
    const capacityStack = root.querySelector('[data-capacity-stack]');
    const capacityStatus = root.querySelector('[data-capacity-status]');
    const summaryShell = root.querySelector('[data-summary-shell]');
    const summaryPanel = root.querySelector('[data-summary-panel]');
    const summaryConclusion = root.querySelector('[data-summary-conclusion]');
    const summaryToggle = root.querySelector('[data-summary-toggle]');
    const summaryToggleLabel = root.querySelector('[data-summary-toggle-label]');
    const resultStacks = [capacityStack, root.querySelector('[data-step-results-stack]')].filter(Boolean);
    const resultSettings = new Map(resultStacks.map(stack => [stack, stack.querySelector('[data-result-settings]')]));
    const disclaimerToggle = root.querySelector('[data-disclaimer-toggle]');
    const mobileResultsMedia = window.matchMedia('(max-width: 699px)');
    const calculatorSection = root.closest('.w21-power-calculator') || root;
    const calculatorContent = calculatorSection.querySelector('[data-calculator-content]');
    const calculatorDisclosure = calculatorSection.querySelector('[data-calculator-disclosure]');
    const calculatorDisclosureLabel = calculatorSection.querySelector('[data-calculator-disclosure-label]');
    const solarDisclosure = root.querySelector('[data-solar-disclosure]');
    const solarOptions = root.querySelector('[data-solar-options]');
    const solarDisclosureLabel = root.querySelector('[data-solar-disclosure-label]');
    const powerDisclosure = root.querySelector('[data-power-disclosure]');
    const expansionDisclosure = root.querySelector('[data-expansion-disclosure]');
    const sunlightDisclosure = sunlightTemplate?.querySelector('[data-sunlight-disclosure]');
    const powerPickerMeta = root.querySelector('[data-power-picker-meta]');
    const solarPickerMeta = root.querySelector('[data-solar-picker-meta]');
    const powerMobileLabel = root.querySelector('[data-power-mobile-label]');
    const solarMobileLabel = root.querySelector('[data-solar-mobile-label]');
    const syncSummaryToggle = () => {
      const expanded = root.classList.contains('is-summary-open');
      summaryToggle?.setAttribute('aria-expanded', String(expanded));
      summaryToggle?.setAttribute('aria-label', expanded ? labels.closeResults : labels.openResults);
      if (summaryToggleLabel) summaryToggleLabel.textContent = expanded ? 'Hide results' : 'Show results';
    };
    syncSummaryToggle();

    const formatNumber = (value, digits = 0) => !Number.isFinite(value) ? '0' : value.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: 0 });
    const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
    const fitCustomNameInput = (input) => {
      if (!input) return;
      input.style.fontSize = '';
      let size = parseFloat(window.getComputedStyle(input).fontSize) || 14;
      const minimumSize = 10;
      while (input.scrollWidth > input.clientWidth && size > minimumSize) {
        size -= 0.5;
        input.style.fontSize = `${size}px`;
      }
    };
    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
    const getSelectedPowerStation = () => powerStations.find((product) => product.id === state.selectedPowerStationId) || null;
    // Recommendations use the same product markup, without mutating selection.
    const getSelectedSetupItems = (selection = state) => {
      const station = powerStations.find(product => product.id === selection.selectedPowerStationId);
      const panel = solarPanels.find(product => product.id === selection.selectedSolarId);
      return station ? [
        { product: station, quantity: 1 },
        ...(selection.expansionCount ? [{ product: expansionBattery, quantity: selection.expansionCount }] : []),
        ...(panel && selection.solarCount ? [{ product: panel, quantity: selection.solarCount }] : [])
      ] : [];
    };
    const getOutputLimit = () => {
      const station = getSelectedPowerStation();
      return station ? station.output : Infinity;
    };
    const isCustomDevice = (device) => device.isCustom === true || device.id === 'custom_device' || device.id.startsWith('custom_');
    const finalizeClonedDevices = (devices, hasPreset) => {
      const groupRank = (device) => {
        const watts = getDevicePower(device);
        return watts <= 100 ? 0 : watts <= 1200 ? 1 : 2;
      };
      const normalized = devices.map((device, sourceIndex) => ({
        ...device,
        active: hasPreset ? Boolean(device.active) : false,
        simultaneous: hasPreset ? Boolean(device.active) : false,
        selectionOrder: null,
        sourceIndex
      }));
      let selectionOrder = 0;

      normalized
        .filter((device) => device.active)
        .sort((a, b) => groupRank(a) - groupRank(b) || a.sourceIndex - b.sourceIndex)
        .forEach((device) => { device.selectionOrder = ++selectionOrder; });

      return normalized;
    };
    // Shared catalogue: identical ratings and usage defaults in every scenario.
    const applianceCatalog = [
      [
        "phone",
        "Mobile",
        "Mobiel",
        "Mobiltelefon",
        20,
        "battery",
        1,
        0
      ],
      [
        "tablet",
        "Tablet",
        "Tablet",
        "Tablet",
        20,
        "battery",
        1,
        30
      ],
      [
        "laptop",
        "Laptop",
        "Laptop",
        "Laptop",
        65,
        "battery",
        1,
        0
      ],
      [
        "camera",
        "Camera",
        "Camera",
        "Kamera",
        40,
        "battery",
        1,
        0
      ],
      [
        "drone",
        "Drone",
        "Drone",
        "Drohne",
        30,
        "battery",
        1,
        30
      ],
      [
        "cpap_machine",
        "CPAP Device",
        "CPAP-Apparaat",
        "CPAP-Gerät",
        50,
        "ac",
        8,
        0
      ],
      [
        "led_lighting",
        "LED Lighting",
        "LED-Verlichting",
        "LED-Beleuchtung",
        10,
        "ac",
        4,
        0
      ],
      [
        "electric_coolbox",
        "Electric Coolbox (14L)",
        "Elektrische Koelbox (14L)",
        "Elektrische Kühlbox (14L)",
        50,
        "dc",
        24,
        0
      ],
      [
        "compressor_cooler_m",
        "Compressor Coolbox (40L)",
        "Compressor-Koelbox (40L)",
        "Kompressor-Kühlbox (40L)",
        45,
        "measured",
        24,
        0
      ],
      [
        "electric_fridge",
        "Electric Fridge (130L)",
        "Elektrische Koelkast (130L)",
        "Elektrischer Kühlschrank (130L)",
        80,
        "ac",
        8,
        0
      ],
      [
        "fridge",
        "Gas Fridge (RV)",
        "Gaskoelkast (Camper)",
        "Gas-Kühlschrank (Wohnmobil)",
        1,
        "dc",
        24,
        0
      ],
      [
        "coffee_machine_camping",
        "Coffee Machine (12V)",
        "Koffiezetapparaat (12V)",
        "Kaffeemaschine (12V)",
        170,
        "dc",
        0,
        15
      ],
      [
        "coffee_machine_pods",
        "Nespresso Machine",
        "Nespresso-Apparaat",
        "Nespresso-Maschine",
        1300,
        "ac",
        0,
        10
      ],
      [
        "mini_electric_kettle",
        "Mini Kettle (12V)",
        "Mini Waterkoker (12V)",
        "Mini-Wasserkocher (12V)",
        200,
        "dc",
        0,
        15
      ],
      [
        "toaster",
        "Toaster",
        "Broodrooster",
        "Toaster",
        750,
        "ac",
        0,
        10
      ],
      [
        "air_fryer",
        "Air Fryer",
        "Airfryer",
        "Heißluftfritteuse",
        1700,
        "ac",
        0,
        20
      ],
      [
        "electric_grill",
        "Electric Grill",
        "Elektrische Grill",
        "Elektrischer Grill",
        1500,
        "ac",
        0,
        30
      ],
      [
        "tyre_inflator",
        "Tyre Inflator",
        "Bandenpomp",
        "Reifenpumpe",
        20,
        "battery",
        1,
        30
      ],
      [
        "led_television",
        "LED Television",
        "LED-Televisie",
        "LED-Fernseher",
        40,
        "ac",
        2,
        0
      ],
      [
        "bike_battery",
        "Bike Battery",
        "Fietsaccu",
        "Fahrradakku",
        170,
        "battery",
        3,
        0
      ],
      [
        "ecomat_heater",
        "Mini Heater",
        "Mini Heater",
        "Mini-Heizgerät",
        1200,
        "ac",
        1,
        0
      ],
      [
        "travel_hair_dryer",
        "Hair Dryer (12V)",
        "Haarföhn (12V)",
        "Haartrockner (12V)",
        120,
        "dc",
        0,
        10
      ],
      [
        "fan",
        "Fan",
        "Ventilator",
        "Ventilator",
        30,
        "ac",
        4,
        0
      ],
      [
        "roof_air_conditioner",
        "Roof Air Conditioner",
        "Dakairco",
        "Dachklimaanlage",
        800,
        "ac",
        1,
        0
      ],
      [
        "music_speaker",
        "Mini Speaker",
        "Mini Speaker",
        "Mini-Lautsprecher",
        20,
        "ac",
        1,
        0
      ],
      [
        "party_speaker",
        "Party Speaker",
        "Partyspeaker",
        "Party-Lautsprecher",
        80,
        "ac",
        4,
        0
      ],
      [
        "flashlight",
        "Flashlight",
        "Zaklamp",
        "Taschenlampe",
        5,
        "battery",
        1,
        0
      ],
      [
        "water_pump",
        "Water Pump",
        "Waterpomp",
        "Wasserpumpe",
        40,
        "dc",
        0,
        30
      ],
      [
        "starlink",
        "Starlink",
        "Starlink",
        "Starlink",
        100,
        "ac",
        4,
        0
      ],
      [
        "wifi_router",
        "Wi-Fi Router",
        "Wifi-Router",
        "WLAN-Router",
        15,
        "ac",
        8,
        0
      ],
      [
        "battery_tool",
        "Battery Tool (18V/5Ah)",
        "Accugereedschap (18V/5Ah)",
        "Akkuwerkzeug (18V/5Ah)",
        90,
        "battery",
        1,
        0
      ],
      [
        "hammer_drill",
        "Hammer Drill",
        "Boorhamer",
        "Bohrhammer",
        800,
        "ac",
        0,
        30
      ],
      [
        "angle_grinder",
        "Angle Grinder",
        "Haakse Slijper",
        "Winkelschleifer",
        900,
        "ac",
        0,
        15
      ],
      [
        "circular_saw",
        "Circular Saw",
        "Cirkelzaag",
        "Kreissäge",
        1400,
        "ac",
        0,
        15
      ],
      [
        "vacuum_cleaner",
        "Vacuum Cleaner",
        "Stofzuiger",
        "Staubsauger",
        1000,
        "ac",
        0,
        30
      ],
      [
        "sander",
        "Sander",
        "Schuurmachine",
        "Schleifmaschine",
        300,
        "ac",
        0,
        30
      ],
      [
        "plunge_router",
        "Plunge Router",
        "Bovenfrees",
        "Oberfräse",
        1200,
        "ac",
        0,
        30
      ],
      [
        "reciprocating_saw",
        "Reciprocating Saw",
        "Reciprozaag",
        "Säbelsäge",
        1100,
        "ac",
        0,
        15
      ],
      [
        "jigsaw",
        "Jigsaw",
        "Decoupeerzaag",
        "Stichsäge",
        650,
        "ac",
        0,
        30
      ],
      [
        "table_saw",
        "Table Saw",
        "Tafelzaag",
        "Tischkreissäge",
        1900,
        "ac",
        0,
        15
      ],
      [
        "led_floodlight",
        "LED Floodlight",
        "LED Werklamp",
        "LED-Arbeitsleuchte",
        30,
        "ac",
        4,
        0
      ]
    ];
    const roundEnergyWh = (value) => value === 1 ? 1 : Math.round(value / 5) * 5;
    const AC_EFFICIENCY = 0.85;
    const CHARGING_EFFICIENCY = 0.80;
    // Retain the previous per-charge estimates; their implied battery capacities
    // are provisional, not verified product specifications.
    const referenceBatteryWh = { phone: 18.4, tablet: 27.2, laptop: 50.4, camera: 12, drone: 56, bike_battery: 500, battery_tool: 90, tyre_inflator: 20 };
    const cloneDevices = (audienceId) => {
      const preset = audiences.find((item) => item.id === audienceId);
      const selected = preset ? scenarioSelections[preset.id] : [];
      const devices = applianceCatalog.map(([id, en, nl, de, watts, energyMode, hours, minutes], index) => {
        const batteryWh = referenceBatteryWh[id];
        return {
          id, name: { en, nl, de }[locale], watts, energyMode,
          applicationType: index >= 30 ? 'pro' : 'household',
          usageType: 'timed',
          batteryWh, chargeWh: batteryWh !== undefined ? roundEnergyWh(batteryWh / CHARGING_EFFICIENCY) : undefined,
          charges: 1, hours, minutes,
          // Already includes power-station losses; do not apply an efficiency again.
          practicalWhPerHour: energyMode === 'measured' ? 35 : undefined,
          active: selected.includes(id), simultaneous: selected.includes(id)
        };
      });
      return finalizeClonedDevices(devices, Boolean(preset));
    };
    const syncSelectionSequence = () => {
      state.selectionSequence = state.devices.reduce((highest, device) => Math.max(highest, Number(device.selectionOrder) || 0), 0);
    };
    // Existing appliances are Household; future tools explicitly opt into Pro.
    const getDeviceGroupId = (device) => device.applicationType === 'pro' ? 'pro' : 'household';
    const getTimeHours = (device) => {
      const hours = clamp(Math.floor(Number(device.hours) || 0), 0, 24);
      return hours === 24 ? 24 : hours + clamp(Math.floor(Number(device.minutes) || 0), 0, 59) / 60;
    };
    let consumptionDialog;
    root.addEventListener('click', event => {
      const trigger = event.target.closest('[data-consumption-help]');
      if (!trigger) return;
      if (!consumptionDialog) {
        consumptionDialog = document.createElement('dialog');
        consumptionDialog.className = 'w21-consumption-dialog';
        const closeLabel = { en: 'Close', nl: 'Sluiten', de: 'Schließen' }[locale];
        consumptionDialog.setAttribute('aria-labelledby', 'w21-consumption-dialog-title');
        const sectionTextStyle = getComputedStyle(root);
        consumptionDialog.style.fontFamily = sectionTextStyle.fontFamily;
        consumptionDialog.style.fontSize = sectionTextStyle.fontSize;
        consumptionDialog.style.color = sectionTextStyle.color;
        consumptionDialog.innerHTML = `<button type="button" class="w21-consumption-dialog-close" aria-label="${closeLabel}"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg></button><h2 id="w21-consumption-dialog-title" class="w21-consumption-dialog-title">${escapeHtml(labels.netDailyNeed)}</h2><p></p>`;
        document.body.appendChild(consumptionDialog);
        consumptionDialog.querySelector('button').addEventListener('click', () => consumptionDialog.close());
        consumptionDialog.addEventListener('click', e => {
          const rect = consumptionDialog.getBoundingClientRect();
          if (e.target === consumptionDialog && (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom)) consumptionDialog.close();
        });
      }
      consumptionDialog.querySelector('.w21-consumption-dialog-title').textContent = trigger.dataset.helpTitle || labels.netDailyNeed;
      consumptionDialog.querySelector('p').textContent = trigger.dataset.helpText;
      consumptionDialog.showModal();
    });
    const getDevicePower = device => Math.max(Number(device.peakWatts ?? device.watts) || 0, 0);
    const getDeviceDailyWh = (device) => {
      const watts = Math.max(Number(device.watts) || 0, 0);
      const efficiency = device.energyMode === 'battery' ? CHARGING_EFFICIENCY : device.energyMode === 'ac' ? AC_EFFICIENCY : 1;
      const calculationWatts = device.energyMode === 'measured' ? watts * 35 / 45 : roundEnergyWh(watts / efficiency);
      return roundEnergyWh(calculationWatts * getTimeHours(device));
    };
    const getSolarWh = () => {
      const panel = solarPanels.find((item) => item.id === state.selectedSolarId);
      return panel ? panel.watts * state.solarSunHours * state.solarCount : 0;
    };
    const getBatteryCapacity = () => { const station = getSelectedPowerStation(); return station ? station.capacity + (state.expansionCount * expansionBattery.capacity) : 0; };
    const getTotals = () => {
      let dailyWh = 0;
      let simultaneousW = 0;
      let highestDeviceW = 0;
      state.devices.forEach((device) => {
        if (!device.active) return;
        const watts = getDevicePower(device);
        dailyWh += getDeviceDailyWh(device);
        highestDeviceW = Math.max(highestDeviceW, watts);
        if (device.simultaneous) simultaneousW += watts;
      });
      // Switching simultaneous use off must still leave enough output for each selected appliance.
      const peakW = Math.max(simultaneousW, highestDeviceW);
      const station = getSelectedPowerStation();
      const outputW = station ? station.output : 0;
      const batteryCapacity = getBatteryCapacity();
      const solarWh = getSolarWh();
      const availableWh = batteryCapacity + solarWh;
      return { dailyWh, peakW, outputW, batteryCapacity, solarWh, availableWh, outputRemaining: outputW - peakW, capacityRemaining: availableWh - dailyWh };
    };
    const getInitials = (name) => name.split(/\s+/).slice(0, 2).map((part) => part.charAt(0)).join('').toUpperCase();
    const getProductImageHtml = (product) => product.image ? `<img src="${product.image}" alt="${product.name}">` : `<span class="w21-power-calculator__product-placeholder">${getInitials(product.name)}</span>`;
    const getSolarMax = (solarId) => {
      const station = getSelectedPowerStation();
      return station ? (solarMaxByStation[station.id]?.[solarId] || 0) : 0;
    };
    const cleanupCompatibility = () => {
      const station = getSelectedPowerStation();
      if (!station) { state.expansionCount = 0; state.selectedSolarId = ''; state.solarCount = 0; return; }
      if (!station.compatibleExpansion) state.expansionCount = 0;
      if (state.selectedSolarId && getSolarMax(state.selectedSolarId) === 0) { state.selectedSolarId = ''; state.solarCount = 0; }
      if (state.selectedSolarId) state.solarCount = clamp(state.solarCount, 0, getSolarMax(state.selectedSolarId));
      if (state.solarCount === 0) state.selectedSolarId = '';
    };



    const setStep = (step, { render = true } = {}) => {
      state.step = Math.min(Math.max(step, 1), 3);
      stepButtons.forEach((button) => { const isActive = Number(button.dataset.stepButton) === state.step; button.classList.toggle('is-active', isActive); button.setAttribute('aria-current', isActive ? 'step' : 'false'); });
      panels.forEach((panel) => panel.classList.toggle('is-active', Number(panel.dataset.stepPanel) === state.step));
      if (render) renderAll();
    };

    const renderAudienceTabs = () => {
      if (!audienceGrid) return;
      audienceGrid.innerHTML = '';
      audiences.forEach((audience) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `w21-power-calculator__preset-tab${audience.id === state.audienceId ? ' is-active' : ''}`;
        button.textContent = audienceNames[audience.id] || audience.name;
        button.addEventListener('click', () => {
          state.audienceId = state.audienceId === audience.id ? '' : audience.id;
          state.customCount = 0;
          state.devices = cloneDevices(state.audienceId);
          syncSelectionSequence();
          
          openDeviceGroups.clear();
          renderAll();
        });
        audienceGrid.appendChild(button);
      });

      if (state.audienceId !== 'custom') return;

      const customButton = document.createElement('span');
      customButton.className = `w21-power-calculator__preset-tab${state.audienceId === 'custom' ? ' is-active' : ''}`;
      customButton.textContent = labels.customSetup;
      customButton.setAttribute('aria-current', 'true');
      audienceGrid.appendChild(customButton);
    };

    const getStationBars = (station) => `<div class="w21-power-calculator__product-bars">
      <div class="w21-power-calculator__product-bar-row">
        <span class="w21-power-calculator__product-bar-row-head"><span>${labels.capacity}</span><strong>${formatNumber(station.capacity)}Wh</strong></span>
        <span class="w21-power-calculator__product-bar"></span>
      </div>
      <div class="w21-power-calculator__product-bar-row">
        <span class="w21-power-calculator__product-bar-row-head"><span>${labels.output}</span><strong>${formatNumber(station.output)}W</strong></span>
        <span class="w21-power-calculator__product-bar w21-power-calculator__product-bar--output"></span>
      </div>
    </div>`;
    const getExpansionBars = () => `<div class="w21-power-calculator__product-bars">
      <div class="w21-power-calculator__product-bar-row">
        <span class="w21-power-calculator__product-bar-row-head"><span>${labels.extraCapacity}</span><strong>${formatNumber(expansionBattery.capacity)}Wh</strong></span>
        <span class="w21-power-calculator__product-bar"></span>
      </div>
    </div>`;
    const getSolarBars = (panel) => `<div class="w21-power-calculator__product-bars">
      <div class="w21-power-calculator__product-bar-row">
        <span class="w21-power-calculator__product-bar-row-head"><span>${labels.expectedSolarInput}</span><strong>+${formatNumber(panel.watts * state.solarSunHours)}Wh/day</strong></span>
        <span class="w21-power-calculator__product-bar w21-power-calculator__product-bar--solar"></span>
      </div>
    </div>`;

    const renderProducts = () => {
      if (!powerProductGrid || !solarProductGrid) return;
      powerProductGrid.innerHTML = '';
      solarProductGrid.innerHTML = '';
      if (state.selectedPowerStationId !== 'move_1800') state.expansionCount = 0;
      let selectedMove1800Card = null;
      const orderedPowerStations = mobileResultsMedia.matches && state.selectedPowerStationId
        ? [...powerStations].sort((a, b) => Number(b.id === state.selectedPowerStationId) - Number(a.id === state.selectedPowerStationId))
        : powerStations;
      orderedPowerStations.forEach((station) => {
        const card = document.createElement('button');
        const isActive = state.selectedPowerStationId === station.id;
        card.type = 'button';
        card.className = `w21-power-calculator__product-card${isActive ? ' is-active' : ''}`;
        card.innerHTML = `<span class="w21-power-calculator__product-media">${getProductImageHtml(station)}</span><span class="w21-power-calculator__product-content"><span class="w21-power-calculator__product-head"><strong class="w21-power-calculator__product-title">${station.name}</strong><span class="w21-power-calculator__product-check" data-product-check>${checkIcon}</span></span>${getStationBars(station)}</span>`;
        card.addEventListener('click', () => {
          const pickerWasOpen = mobileResultsMedia.matches && root.classList.contains('is-power-picker-open');
          if (mobileResultsMedia.matches && !pickerWasOpen && state.selectedPowerStationId !== station.id) return;
          if (pickerWasOpen) {
            root.classList.remove('is-power-picker-open');
            powerDisclosure?.setAttribute('aria-expanded', 'false');
          }
          selectionIsManual = true;

          if (state.selectedPowerStationId === station.id) {
            state.selectedPowerStationId = '';
            state.expansionCount = 0;
            state.selectedSolarId = '';
            state.solarCount = 0;
          } else {
            state.selectedPowerStationId = station.id;
            cleanupCompatibility();
          }
          renderAll();
        });
        powerProductGrid.appendChild(card);
        if (station.id === 'move_1800' && isActive) selectedMove1800Card = card;
      });

      const station = getSelectedPowerStation();
      if (powerPickerMeta) powerPickerMeta.textContent = station ? labels.change : (powerStations.length === 3 ? labels.threeOptions : `${powerStations.length}`);
      if (powerMobileLabel) powerMobileLabel.textContent = station ? labels.switchPowerStation : labels.addPowerStation;
      powerDisclosure?.classList.toggle('has-selection', Boolean(station));
      if (solarPickerMeta) {
        const compatibleSolarCount = solarPanels.filter((panel) => getSolarMax(panel.id) > 0).length;
        solarPickerMeta.textContent = state.selectedSolarId ? labels.change : (station ? `${compatibleSolarCount} ${labels.compatibleCount}` : labels.selectPowerFirst);
      }
      if (solarMobileLabel) solarMobileLabel.textContent = state.selectedSolarId ? labels.switchSolarPanels : labels.addSolarPanels;
      solarDisclosure?.classList.toggle('has-selection', Boolean(state.selectedSolarId));
      if (station?.id === 'move_1800' && selectedMove1800Card) {
        const expansionCard = document.createElement('div');
        expansionCard.className = `w21-power-calculator__product-card w21-power-calculator__product-card--expansion${state.expansionCount > 0 ? ' is-active' : ''}`;
        expansionCard.innerHTML = `<span class="w21-power-calculator__product-media">${getProductImageHtml(expansionBattery)}</span><span class="w21-power-calculator__product-content"><span class="w21-power-calculator__product-head"><strong class="w21-power-calculator__product-title">${expansionBattery.name}</strong><span class="w21-power-calculator__product-check" data-product-check>${checkIcon}</span></span>${getExpansionBars()}<span class="w21-power-calculator__expansion-controls"><button type="button" class="w21-power-calculator__quantity-button" data-expansion-minus aria-label="${labels.decreaseExpansion}" ${state.expansionCount === 0 ? 'disabled' : ''}>${minusIcon}</button><strong>${state.expansionCount} / ${expansionBattery.maxQuantity}</strong><button type="button" class="w21-power-calculator__quantity-button" data-expansion-plus aria-label="${labels.increaseExpansion}" ${state.expansionCount >= expansionBattery.maxQuantity ? 'disabled' : ''}>${plusIcon}</button></span></span>`;
        expansionCard.querySelector('[data-expansion-minus]')?.addEventListener('click', () => changeSetupQuantity('expansion', -1));
        expansionCard.querySelector('[data-expansion-plus]')?.addEventListener('click', () => changeSetupQuantity('expansion', 1));
        expansionCard.addEventListener('click', (event) => {
          if (event.target.closest('button')) return;
          selectionIsManual = true;

          if (mobileResultsMedia.matches) {
            state.expansionCount = state.expansionCount > 0 ? 0 : 1;
            renderAll();
            return;
          }

          if (event.target.closest('[data-product-check]')) {
            state.expansionCount = state.expansionCount > 0 ? 0 : 1;
            renderAll();
            return;
          }

          if (state.expansionCount === 0) {
            state.expansionCount = 1;
          } else if (state.expansionCount === 1) {
            state.expansionCount = 0;
          }

          renderAll();
        });
        powerProductGrid.appendChild(expansionCard);
      }

      if (expansionDisclosure) expansionDisclosure.hidden = !(station?.id === 'move_1800' && state.expansionCount === 0);

      const orderedSolarPanels = mobileResultsMedia.matches && state.selectedSolarId
        ? [...solarPanels].sort((a, b) => Number(b.id === state.selectedSolarId) - Number(a.id === state.selectedSolarId))
        : solarPanels;
      orderedSolarPanels.forEach((panel) => {
        const maxQuantity = getSolarMax(panel.id);
        const isSelectedType = state.selectedSolarId === panel.id;
        const compatible = maxQuantity > 0;
        const isActive = isSelectedType && state.solarCount > 0;
        const card = document.createElement('div');
        card.className = `w21-power-calculator__product-card${isActive ? ' is-active' : ''}${!compatible ? ' is-disabled' : ''}`;
        card.innerHTML = `<span class="w21-power-calculator__product-media">${getProductImageHtml(panel)}</span><span class="w21-power-calculator__product-content"><span class="w21-power-calculator__product-head"><strong class="w21-power-calculator__product-title">${panel.name}</strong><span class="w21-power-calculator__product-check" data-product-check>${checkIcon}</span></span>${getSolarBars(panel)}<span class="w21-power-calculator__solar-controls"><button type="button" class="w21-power-calculator__quantity-button" data-solar-minus aria-label="${labels.decreaseSolar}" ${!isActive ? 'disabled' : ''}>${minusIcon}</button><strong>${isSelectedType ? state.solarCount : 0} / ${maxQuantity}</strong><button type="button" class="w21-power-calculator__quantity-button" data-solar-plus aria-label="${labels.increaseSolar}" ${!compatible || (isSelectedType && state.solarCount >= maxQuantity) ? 'disabled' : ''}>${plusIcon}</button></span></span>`;
        card.querySelector('[data-solar-minus]')?.addEventListener('click', () => changeSetupQuantity('solar', -1, panel.id));
        card.querySelector('[data-solar-plus]')?.addEventListener('click', () => changeSetupQuantity('solar', 1, panel.id));
        card.addEventListener('click', (event) => {
          if (!compatible || event.target.closest('button')) return;

          const pickerWasOpen = mobileResultsMedia.matches && root.classList.contains('is-solar-picker-open');
          if (mobileResultsMedia.matches && !pickerWasOpen && !isSelectedType) return;
          selectionIsManual = true;
          if (pickerWasOpen) {
            root.classList.remove('is-solar-picker-open');
            solarDisclosure?.setAttribute('aria-expanded', 'false');
          }

          if (isSelectedType) {
            state.selectedSolarId = '';
            state.solarCount = 0;
            rememberedSolarSelection = null;
            renderAll();
            return;
          }

          state.selectedSolarId = panel.id;
          state.solarCount = 1;
          syncSolarAdvice(true);

          renderAll();
        });
        solarProductGrid.appendChild(card);
      });

      if (mobileResultsMedia.matches && !root.classList.contains('is-solar-picker-open')) {
        solarOptions.hidden = !state.selectedSolarId;
      }
    };

    const renderDeviceGrid = () => {
      if (!deviceGrid) return;

      const limit = getOutputLimit();
      const limited = Number.isFinite(limit);
      const focused = document.activeElement;
      applianceDragCleanup?.();
      catalogueResizeObserver?.disconnect();
      window.cancelAnimationFrame(catalogueAnimationFrame);
      catalogueAnimationFrame = 0;
      deviceGrid.innerHTML = '';

      const workspaceLabels = {
        en: { selection: 'Your appliances', browse: 'Add appliances', hide: 'Hide appliance list', search: 'Search appliances…', empty: 'Drag & drop here', emptyMobile: 'Open the appliance list to add your appliances.', noMatches: 'No appliances found. Try another search or add a custom appliance.', household: 'Household', pro: 'Pro Tools' },
        nl: { selection: 'Jouw apparaten', browse: 'Voeg apparaten toe', hide: 'Verberg apparatenlijst', search: 'Zoek apparaten…', empty: 'Drag & drop here', emptyMobile: 'Open de apparatenlijst om je apparaten toe te voegen.', noMatches: 'Geen apparaten gevonden. Zoek opnieuw of voeg een eigen apparaat toe.', household: 'Huishoudelijk', pro: 'Gereedschap' },
        de: { selection: 'Deine Geräte', browse: 'Geräte hinzufügen', hide: 'Geräteliste ausblenden', search: 'Geräte suchen…', empty: 'Drag & drop here', emptyMobile: 'Öffne die Geräteliste, um Geräte hinzuzufügen.', noMatches: 'Keine Geräte gefunden. Suche erneut oder füge ein eigenes Gerät hinzu.', household: 'Haushalt', pro: 'Werkzeuge' }
      }[locale];
      const focusedCatalogueId = focused?.closest('[data-catalogue-item]')?.dataset.catalogueItem;
      const selectedDevices = state.devices.filter(device => device.active);
      const selectedSection = document.createElement('section');
      selectedSection.className = 'w21-power-calculator__appliance-selection';
      selectedSection.dataset.applianceSelection = '';
      selectedSection.setAttribute('aria-label', workspaceLabels.selection);
      selectedSection.innerHTML = `<div class="w21-power-calculator__appliance-dropzone"><svg class="w21-power-calculator__dropzone-outline" aria-hidden="true" focusable="false"><rect/></svg><div class="w21-power-calculator__catalogue-scroll-region w21-power-calculator__selection-scroll-region"><div class="w21-power-calculator__selection-list" data-selection-scroll-list><div class="w21-power-calculator__selected-device-grid" data-selection-grid></div><div class="w21-power-calculator__selection-empty"${selectedDevices.length ? ' hidden' : ''}><div class="w21-power-calculator__selection-empty-prompt"><span class="w21-power-calculator__solar-disclosure-icon" aria-hidden="true">${plusIcon}</span><p class="w21-power-calculator__selection-desktop-hint">${workspaceLabels.empty}</p><p class="w21-power-calculator__selection-mobile-hint">${workspaceLabels.emptyMobile}</p></div><button type="button" class="w21-power-calculator__catalogue-empty-action">${workspaceLabels.browse}</button></div></div><button type="button" class="w21-power-calculator__catalogue-scroll-arrow is-up" data-selection-scroll="up" aria-label="Scroll selected appliances up" hidden><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 14L10 5l7 9Z"/></svg></button><button type="button" class="w21-power-calculator__catalogue-scroll-arrow is-down" data-selection-scroll="down" aria-label="Scroll selected appliances down" hidden><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 6l7 9 7-9Z"/></svg></button></div></div>`;
      const selectionGrid = selectedSection.querySelector('[data-selection-grid]');
      const dropzone = selectedSection.querySelector('.w21-power-calculator__appliance-dropzone');
      const selectionScroll = selectedSection.querySelector('[data-selection-scroll-list]');
      const selectionArrows = [...selectedSection.querySelectorAll('[data-selection-scroll]')];
      selectionArrows.forEach(button => {
        button.addEventListener('click', () => {
          const direction = button.dataset.selectionScroll === 'up' ? -1 : 1;
          selectionScroll.scrollBy({ top: direction * Math.max(56, selectionScroll.clientHeight / 3), behavior: 'auto' });
        });
      });

      const catalogue = document.createElement('aside');
      catalogue.className = 'w21-power-calculator__appliance-catalogue';
      catalogue.dataset.applianceCatalogue = '';
      const catalogueId = `w21-appliance-catalogue-${root.dataset.sectionId || 'calculator'}`;
      catalogue.setAttribute('aria-label', workspaceLabels.browse);
      catalogue.innerHTML = `<button type="button" class="w21-power-calculator__catalogue-mobile-toggle" aria-expanded="${mobileCatalogueOpen}" aria-controls="${catalogueId}"><span class="w21-power-calculator__solar-disclosure-icon" aria-hidden="true">${mobileCatalogueOpen ? minusIcon : plusIcon}</span><strong>${mobileCatalogueOpen ? workspaceLabels.hide : workspaceLabels.browse}</strong></button><div class="w21-power-calculator__catalogue-body" id="${catalogueId}"><label class="w21-power-calculator__catalogue-search"><span class="sr-only">${workspaceLabels.search}</span><input type="search" data-appliance-search placeholder="${workspaceLabels.search}" value="${escapeHtml(applianceSearch)}"></label><div class="w21-power-calculator__catalogue-scroll-region"><div class="w21-power-calculator__catalogue-list w21-power-calculator__catalogue-device-grid" role="region" aria-label="${labels.appliances}"></div><button type="button" class="w21-power-calculator__catalogue-scroll-arrow is-up" data-catalogue-scroll="up" aria-label="Scroll appliances up"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 14L10 5l7 9Z"/></svg></button><button type="button" class="w21-power-calculator__catalogue-scroll-arrow is-down" data-catalogue-scroll="down" aria-label="Scroll appliances down"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 6l7 9 7-9Z"/></svg></button></div><p class="w21-power-calculator__catalogue-empty" data-catalogue-empty hidden>${workspaceLabels.noMatches}</p></div>`;
      // Source list first in visual, reading and keyboard order.
      deviceGrid.append(catalogue, selectedSection);
      deviceGrid.classList.toggle('is-catalogue-open', mobileCatalogueOpen);
      const searchInput = catalogue.querySelector('[data-appliance-search]');
      const list = catalogue.querySelector('.w21-power-calculator__catalogue-list');
      const scrollArrows = [...catalogue.querySelectorAll('[data-catalogue-scroll]')];
      scrollArrows.forEach(button => {
        button.addEventListener('click', () => {
          const direction = button.dataset.catalogueScroll === 'up' ? -1 : 1;
          list.scrollBy({ top: direction * Math.max(56, list.clientHeight / 3), behavior: 'auto' });
        });
      });
      const setMobileCatalogue = (open) => {
        mobileCatalogueOpen = open;
        deviceGrid.classList.toggle('is-catalogue-open', open);
        const toggle = catalogue.querySelector('.w21-power-calculator__catalogue-mobile-toggle');
        toggle.setAttribute('aria-expanded', String(open));
        toggle.querySelector('span').innerHTML = open ? minusIcon : plusIcon;
        toggle.querySelector('strong').textContent = open ? workspaceLabels.hide : workspaceLabels.browse;
        if (open) searchInput.focus({ preventScroll: true });
        else toggle.focus({ preventScroll: true });
        queueCatalogueLayout();
        queueSummaryPosition();
      };
      catalogue.querySelector('.w21-power-calculator__catalogue-mobile-toggle').addEventListener('click', () => setMobileCatalogue(!mobileCatalogueOpen));
      selectedSection.querySelector('.w21-power-calculator__catalogue-empty-action').addEventListener('click', () => {
        setMobileCatalogue(true);
        searchInput.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      const applyScrollFade = (scrollArea, items, hasAbove, hasBelow) => {
        const viewport = scrollArea.getBoundingClientRect();
        const visible = items.filter(item => {
          const box = item.getBoundingClientRect();
          return box.bottom > viewport.top + 1 && box.top < viewport.bottom - 1;
        });
        const firstTop = visible[0]?.getBoundingClientRect().top;
        const lastTop = visible[visible.length - 1]?.getBoundingClientRect().top;
        items.forEach(item => {
          const box = item.getBoundingClientRect();
          const fadeTop = hasAbove && visible.includes(item) && Math.abs(box.top - firstTop) <= 1;
          const fadeBottom = hasBelow && visible.includes(item) && Math.abs(box.top - lastTop) <= 1;
          item.style.opacity = fadeTop || fadeBottom ? '.75' : '1';
          if (!fadeTop && !fadeBottom) {
            item.style.maskImage = item.style.webkitMaskImage = 'none';
            return;
          }
          const topEdge = Math.max(0, viewport.top - box.top);
          const bottomEdge = Math.min(box.height, viewport.bottom - box.top);
          const band = Math.min(48, (bottomEdge - topEdge) / 2);
          const stops = [fadeTop ? `transparent 0px, transparent ${topEdge}px, #000 ${topEdge + band}px` : '#000 0px'];
          stops.push(fadeBottom ? `#000 ${bottomEdge - band}px, transparent ${bottomEdge}px, transparent 100%` : '#000 100%');
          item.style.maskImage = item.style.webkitMaskImage = `linear-gradient(to bottom, ${stops.join(', ')})`;
        });
      };
      const updateCatalogueLayout = () => {
        const sideBySide = window.matchMedia('(min-width: 1100px)').matches;
        if (!sideBySide) { dropzone.style.height = ''; dropzone.style.minHeight = ''; }
        if (!list.isConnected || !list.clientWidth) return;
        const viewport = list.getBoundingClientRect();
        const items = [...list.querySelectorAll('[data-catalogue-item]:not([hidden])')];
        const rows = [];
        items.forEach(item => {
          const box = item.getBoundingClientRect();
          const top = Math.round(box.top - viewport.top + list.scrollTop);
          let row = rows.find(entry => Math.abs(entry.top - top) <= 1);
          if (!row) { row = { top, bottom: top }; rows.push(row); }
          row.bottom = Math.max(row.bottom, box.bottom - viewport.top + list.scrollTop);
        });
        rows.sort((a, b) => a.top - b.top);
        // Fit full rows to the viewport budget; Custom counts as the first row.
        const heightBudget = Math.max(260, Math.min(420, window.innerHeight * .45));
        const rowsThatFit = rows.filter(row => row.bottom <= heightBudget + 1).length;
        const visibleRowCount = Math.min(rows.length, Math.max(1, Math.min(3, rowsThatFit)));
        list.dataset.visibleRows = String(visibleRowCount);
        list.style.maxHeight = rows.length > visibleRowCount ? `${Math.ceil(rows[visibleRowCount - 1].bottom)}px` : 'none';
        if (restoreCatalogueScroll) {
          list.scrollTop = catalogueScrollTop;
          restoreCatalogueScroll = false;
        }
        const hasAbove = list.scrollTop > 1;
        const hasBelow = list.scrollHeight - list.clientHeight - list.scrollTop > 1;
        list.dataset.scrollAbove = String(hasAbove);
        list.dataset.scrollBelow = String(hasBelow);
        scrollArrows.forEach(button => {
          button.hidden = !hasAbove && !hasBelow;
          button.disabled = button.dataset.catalogueScroll === 'up' ? !hasAbove : !hasBelow;
        });
        applyScrollFade(list, items, hasAbove, hasBelow);
        if (sideBySide) {
          dropzone.style.minHeight = '0';
          dropzone.style.height = `${Math.ceil(catalogue.getBoundingClientRect().height)}px`;
        }
        if (restoreSelectionScroll) {
          selectionScroll.scrollTop = selectionScrollTop;
          restoreSelectionScroll = false;
        }
        const selectionHasAbove = selectionScroll.scrollTop > 1;
        const selectionHasBelow = selectionScroll.scrollHeight - selectionScroll.clientHeight - selectionScroll.scrollTop > 1;
        applyScrollFade(selectionScroll, [...selectionGrid.children], selectionHasAbove, selectionHasBelow);
        selectionArrows.forEach(button => {
          button.hidden = !selectionHasAbove && !selectionHasBelow;
          button.disabled = button.dataset.selectionScroll === 'up' ? !selectionHasAbove : !selectionHasBelow;
        });
      };
      const queueCatalogueLayout = () => {
        if (catalogueAnimationFrame) return;
        catalogueAnimationFrame = window.requestAnimationFrame(() => {
          catalogueAnimationFrame = 0;
          updateCatalogueLayout();
        });
      };
      catalogueLayoutRefresh = queueCatalogueLayout;
      let restoreCatalogueScroll = true;
      let restoreSelectionScroll = true;
      selectionScroll.addEventListener('scroll', () => {
        selectionScrollTop = selectionScroll.scrollTop;
        queueCatalogueLayout();
      }, { passive: true });
      list.addEventListener('scroll', () => {
        catalogueScrollTop = list.scrollTop;
        queueCatalogueLayout();
      }, { passive: true });
      const applyCatalogueFilter = (resetScroll = false) => {
        const query = applianceSearch.trim().toLocaleLowerCase(locale);
        let matches = 0;
        list.querySelectorAll('[data-catalogue-item]').forEach(item => {
          const isCustomAction = item.dataset.catalogueItem === 'custom';
          item.hidden = !isCustomAction && Boolean(query && !item.dataset.searchName.includes(query));
          if (!isCustomAction && !item.hidden) matches += 1;
        });
        catalogue.querySelector('[data-catalogue-empty]').hidden = matches > 0;
        if (resetScroll) {
          catalogueScrollTop = 0;
          list.scrollTop = 0;
        }
        queueCatalogueLayout();
      };
      searchInput.addEventListener('input', () => {
        applianceSearch = searchInput.value;
        applyCatalogueFilter(true);
        queueSummaryPosition();
      });
      const customItem = document.createElement('div');
      customItem.dataset.catalogueItem = 'custom';
      customItem.dataset.sourceIndex = '-1';
      const addCustomButton = document.createElement('button');
      addCustomButton.type = 'button';
      addCustomButton.className = 'w21-power-calculator__solar-disclosure-button w21-power-calculator__catalogue-add-custom';
      addCustomButton.setAttribute('aria-label', labels.addCustomDevice);
      const customTitle = { en: 'Custom appliance', nl: 'Eigen apparaat', de: 'Eigenes Gerät' }[locale];
      addCustomButton.innerHTML = `<span class="w21-power-calculator__solar-disclosure-icon" aria-hidden="true">${plusIcon}</span><span>${customTitle}</span>`;
      addCustomButton.addEventListener('click', () => {
        if (mobileResultsMedia.matches) mobileCatalogueOpen = false;
        addCustomDevice();
      });
      customItem.appendChild(addCustomButton);
      list.appendChild(customItem);
      catalogueResizeObserver = new ResizeObserver(() => {
        if (!root.isConnected) { catalogueResizeObserver?.disconnect(); return; }
        queueCatalogueLayout();
      });
      catalogueResizeObserver.observe(list);

      // Pointer-based dragging avoids the operating system's forbidden-drop
      // cursor and draws the exact card DOM instead of a padded native bitmap.
      const setupApplianceDrag = (item, catalogueCard, onDrop) => {
        item.draggable = false;
        item.addEventListener('dragstart', event => event.preventDefault());
        let cancelledRelease = false;
        item.addEventListener('click', event => {
          if (!cancelledRelease || !event.isTrusted || event.detail === 0) return;
          cancelledRelease = false;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, true);
        item.addEventListener('pointerdown', down => {
          cancelledRelease = false;
          if (down.button !== 0 || down.pointerType === 'touch' || down.target.closest('button, input, label')) return;
          applianceDragCleanup?.();
          const controller = new AbortController();
          const options = { capture: true, signal: controller.signal };
          const sourceBox = catalogueCard.getBoundingClientRect();
          const offsetX = down.clientX - sourceBox.left;
          const offsetY = down.clientY - sourceBox.top;
          let started = false;
          let finished = false;
          let ghost = null;
          const isOverDropzone = (x, y) => {
            const box = dropzone.getBoundingClientRect();
            return x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;
          };
          const cleanup = () => {
            if (finished) return;
            finished = true;
            controller.abort();
            if (item.hasPointerCapture(down.pointerId)) item.releasePointerCapture(down.pointerId);
            ghost?.remove();
            catalogueCard.classList.remove('is-drag-source');
            catalogueCard.classList.toggle('is-selection-preview', down.pointerType !== 'touch' && catalogueCard.matches(':hover'));
            document.documentElement.classList.remove('w21-appliance-dragging');
            dropzone.classList.remove('is-drag-over');
            draggedApplianceId = '';
            applianceDragCleanup = null;
          };
          applianceDragCleanup = cleanup;
          window.addEventListener('pointermove', move => {
            if (move.pointerId !== down.pointerId) return;
            if (!item.isConnected) { cleanup(); return; }
            if (!started && Math.hypot(move.clientX - down.clientX, move.clientY - down.clientY) < 6) return;
            move.preventDefault();
            if (!started) {
              started = true;
              draggedApplianceId = item.dataset.catalogueItem;
              item.setPointerCapture(down.pointerId);
              catalogueCard.classList.add('is-drag-source', 'is-selection-preview');
              ghost = catalogueCard.cloneNode(true);
              ghost.classList.remove('is-drag-source');
              ghost.classList.add('w21-power-calculator__appliance-drag-preview', 'is-selection-preview');
              ghost.removeAttribute('data-device-id');
              ghost.setAttribute('aria-hidden', 'true');
              ghost.inert = true;
              ghost.style.width = `${sourceBox.width}px`;
              ghost.style.height = `${sourceBox.height}px`;
              root.appendChild(ghost);
              document.documentElement.classList.add('w21-appliance-dragging');
            }
            ghost.style.transform = `translate3d(${move.clientX - offsetX}px, ${move.clientY - offsetY}px, 0)`;
            dropzone.classList.toggle('is-drag-over', isOverDropzone(move.clientX, move.clientY));
          }, options);
          window.addEventListener('pointerup', up => {
            if (up.pointerId !== down.pointerId) return;
            const shouldAdd = started && isOverDropzone(up.clientX, up.clientY);
            if (started) {
              up.preventDefault();
              // Ignore only the compatibility click generated at this release
              // position, never a later intentional click elsewhere.
              const ignoreReleaseClick = click => {
                if (click.isTrusted && Math.abs(click.clientX - up.clientX) < 1 && Math.abs(click.clientY - up.clientY) < 1 && click.timeStamp - up.timeStamp < 120) {
                  click.preventDefault();
                  click.stopImmediatePropagation();
                  document.removeEventListener('click', ignoreReleaseClick, true);
                }
              };
              document.addEventListener('click', ignoreReleaseClick, true);
              window.setTimeout(() => document.removeEventListener('click', ignoreReleaseClick, true), 120);
            }
            cleanup();
            if (shouldAdd) onDrop();
          }, options);
          const cancelDrag = () => { cancelledRelease = started; cleanup(); };
          window.addEventListener('pointercancel', cancelDrag, options);
          window.addEventListener('blur', event => {
            if (event.target === window) cancelDrag();
          }, options);
          window.addEventListener('keydown', key => {
            if (key.key === 'Escape') { key.preventDefault(); cancelDrag(); }
          }, options);
        });
      };

      const devicesForDisplay = [...state.devices].sort((a, b) => {
        if (a.active !== b.active) return Number(b.active) - Number(a.active);
        if (a.active && b.active) {
          return Number(isCustomDevice(a)) - Number(isCustomDevice(b)) || (Number(a.selectionOrder) || Number.MAX_SAFE_INTEGER) - (Number(b.selectionOrder) || Number.MAX_SAFE_INTEGER);
        }
        return (a.sourceIndex ?? 0) - (b.sourceIndex ?? 0);
      });

      const createDeviceCard = (device, expanded = false) => {
        const isCustom = isCustomDevice(device);
        const watts = Math.max(Number(device.watts) || 0, 0);
        const isIncompatible = false;

        const card = document.createElement('div');
        const hours = Math.max(Number(device.hours) || 0, 0);
        const minutes = clamp(Math.max(Number(device.minutes) || 0, 0), 0, 59);
        const charges = Math.max(Number(device.charges) || 0, 0);
        const customLimitMessage = !isCustom && isIncompatible
          ? `<small class="w21-power-calculator__device-limit">${formatNumber(watts)}W · Max ${formatNumber(limit)}W</small>`
          : '';
        const wattsMaxAttribute = ' max="99999"';
        const powerControl = `<label class="w21-power-calculator__field"><span>${labels.watts}</span><input type="number" min="0"${wattsMaxAttribute} step="1" value="${watts}" data-field="watts"></label>`;
        const usageControls = device.usageType === 'charges'
          ? `<label class="w21-power-calculator__field"><span>${labels.chargesPerDay}</span><input type="number" min="0" step="0.25" inputmode="decimal" value="${charges}" data-field="charges"></label>`
          : `<label class="w21-power-calculator__field"><span>${labels.hours}</span><input type="number" min="0" max="24" step="1" value="${hours}" data-field="hours"></label><label class="w21-power-calculator__field"><span>${labels.minutes}</span><input type="number" min="0" max="${hours >= 24 ? 0 : 59}" step="1" value="${hours >= 24 ? 0 : minutes}" data-field="minutes"${hours >= 24 ? ' disabled' : ''}></label>`;

        const defaultCustomName = device.defaultName || `${labels.customDeviceName} ${state.customCount || 1}`;
        const titleContent = isCustom && device.active
          ? `<input class="w21-power-calculator__device-name-input${device.nameCustomized ? '' : ' is-default'}" type="text" value="${escapeHtml(device.name)}" data-field="name" data-default-name="${escapeHtml(defaultCustomName)}" aria-label="Custom appliance name">`
          : `<span class="w21-power-calculator__device-name">${escapeHtml(device.name)}</span>`;

        card.className = `w21-power-calculator__device${device.active ? ' is-active' : ''}${expanded ? ' is-expanded' : ''}${isIncompatible ? ' is-incompatible' : ''}`;
        card.dataset.deviceId = device.id;
        card.innerHTML = `<div class="w21-power-calculator__device-main"><div class="w21-power-calculator__device-title">${titleContent}</div>${device.active ? customLimitMessage : ''}</div><button type="button" class="w21-power-calculator__switch" aria-label="${escapeHtml(device.name)}" aria-pressed="${device.active ? 'true' : 'false'}"${isIncompatible && !device.active ? ' disabled aria-disabled="true"' : ''}></button><div class="w21-power-calculator__device-controls${device.usageType === 'charges' ? ' is-charges' : ' is-timed'}">${powerControl}${usageControls}</div>`;

        const selectionButton = card.querySelector('.w21-power-calculator__switch');
        selectionButton.className = 'w21-power-calculator__device-select';
        selectionButton.dataset.deviceSelect = '';
        selectionButton.setAttribute('role', 'checkbox');
        selectionButton.setAttribute('aria-checked', String(device.active));
        selectionButton.removeAttribute('aria-pressed');
        selectionButton.innerHTML = device.active ? checkIcon : '';
        if (device.active || expanded) {
          const simultaneousLabel = { en: 'Use simultaneously', nl: 'Gelijktijdig gebruiken', de: 'Gleichzeitig verwenden' }[locale];
          const row = document.createElement('div');
          row.className = 'w21-power-calculator__device-simultaneous';
          row.innerHTML = `<span>${simultaneousLabel}</span><button type="button" class="w21-power-calculator__switch" data-device-simultaneous role="switch" aria-label="${simultaneousLabel}: ${escapeHtml(device.name)}" aria-checked="${device.simultaneous}"></button><button type="button" class="w21-power-calculator__consumption-help" data-consumption-help aria-haspopup="dialog" aria-label="${simultaneousLabel} info"><svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor"/><g transform="translate(2 2) scale(.8)"><path d="M7.5 7a2.5 2.5 0 0 1 5 .2c0 1.8-2.5 1.8-2.5 3.6" fill="none" stroke="#fff" stroke-width="1.7" stroke-linecap="round"/><circle cx="10" cy="14.3" r="1" fill="#fff"/></g></svg></button>`;
          const info = row.querySelector('[data-consumption-help]');
          row.querySelector(':scope > span').remove();
          row.insertBefore(info, row.firstElementChild);
          info.dataset.helpTitle = simultaneousLabel;
          info.dataset.helpText = {
            en: 'Switch on for appliance(s) you use at the same time. Their wattages are added together. Switching off does not remove daily energy consumption. Each appliance must still fit within the power station output.',
            nl: 'Zet aan voor apparaten die je tegelijk gebruikt. Hun vermogens worden opgeteld. Uitzetten verwijdert het dagelijkse energieverbruik niet. Elk apparaat moet afzonderlijk binnen het vermogen van de powerstation passen.',
            de: 'Aktiviere Geräte die gleichzeitig laufen. Ihre Leistungen werden addiert. Ausschalten entfernt den täglichen Energieverbrauch nicht. Jedes Gerät muss einzeln zur Ausgangsleistung passen.'
          }[locale];
          row.querySelector('[data-device-simultaneous]').addEventListener('click', () => {
            if (!device.active) {
              selectionButton.click();
              return;
            }
            markCustomScenario();
            device.simultaneous = !device.simultaneous;
            device.simultaneousConfigured = true;
            renderAll();
          });
          card.insertBefore(row, selectionButton);
        }
        card.querySelectorAll('.w21-power-calculator__device-controls .w21-power-calculator__field').forEach(field => {
          const input = field.querySelector('input');
          const label = field.querySelector('span');
          input.setAttribute('aria-label', label.textContent);
          field.className = 'w21-power-calculator__device-input-field';
          input.classList.add('w21-power-calculator__solar-region-hours');
          label.className = 'w21-power-calculator__device-field-label';
          const valueBox = document.createElement('span');
          valueBox.className = 'w21-power-calculator__solar-hours-field w21-power-calculator__device-value';
          field.appendChild(valueBox);
          valueBox.appendChild(input);
          const unit = document.createElement('span');
          unit.className = 'w21-power-calculator__device-unit';
          unit.setAttribute('aria-hidden', 'true');
          unit.textContent = { watts: 'W', hours: 'h', minutes: 'm', charges: '×' }[input.dataset.field];
          valueBox.appendChild(unit);
          addNumberStepper(input);
        });
        selectionButton.addEventListener('click', () => {
          if (isIncompatible && !device.active) return;
          markCustomScenario();
          if (isCustomDevice(device) && device.active) {
            state.devices = state.devices.filter((item) => item.id !== device.id);
            renderAll();
            return;
          }
          device.active = !device.active;

          if (device.active) {
            if (!device.simultaneousConfigured) device.simultaneous = true;
            device.selectionOrder = ++state.selectionSequence;
          } else {
            device.simultaneous = false;
            device.simultaneousConfigured = false;
            device.selectionOrder = null;
            openOnlyDeviceGroup(getDeviceGroupId(device));
          }

          renderAll();
        });

        card.querySelectorAll('input').forEach((input) => input.addEventListener('input', () => {
          markCustomScenario();
          renderAudienceTabs();
          const field = input.dataset.field;
          if (field === 'name') {
            device.name = input.value;
            device.nameCustomized = input.value !== input.dataset.defaultName;
            input.classList.toggle('is-default', !device.nameCustomized);
            fitCustomNameInput(input);
            
            return;
          }
          const numericValue = Number(input.value || '0');
          let value = Number.isFinite(numericValue) ? Math.max(field === 'charges' ? numericValue : Math.floor(numericValue), 0) : 0;

          const fieldMax = { watts: 99999, hours: 24, minutes: device.hours >= 24 ? 0 : 59 }[field];
          if (fieldMax !== undefined) {
            value = clamp(value, 0, fieldMax);
            if (input.value !== '' && numericValue !== value) input.value = String(value);
          }
          device[field] = value;
          if (field === 'hours') {
            const minutesInput = card.querySelector('[data-field="minutes"]');
            if (value === 24) {
              device.minutes = 0;
              minutesInput.value = '0';
            }
            minutesInput.max = value === 24 ? '0' : '59';
            minutesInput.disabled = value === 24;
          }

          // Synchronize both views without rebuilding the input being typed.
          deviceGrid.querySelectorAll('[data-device-id], [data-catalogue-device-id]').forEach(otherCard => {
            if (otherCard === card || (otherCard.dataset.deviceId || otherCard.dataset.catalogueDeviceId) !== device.id) return;
            otherCard.querySelectorAll('input[data-field]').forEach(otherInput => {
              otherInput.value = String(device[otherInput.dataset.field] ?? '');
              if (otherInput.dataset.field === 'minutes') {
                otherInput.max = device.hours >= 24 ? '0' : '59';
                otherInput.disabled = device.hours >= 24;
              }
            });
          });
          renderStatus();
          renderResults();
        }));

        card.querySelectorAll('input[data-field="watts"], input[data-field="hours"], input[data-field="minutes"]').forEach(input => {
          input.addEventListener('blur', () => { input.value = String(device[input.dataset.field]); });
        });

        return card;
      };

      // Both columns use the same accepted card/value markup and handlers.
      devicesForDisplay.forEach(device => {
        const isCustom = isCustomDevice(device);
        if (device.active) selectionGrid.appendChild(createDeviceCard(device));
        if (!isCustom && !device.active) {
          const item = document.createElement('div');
          item.dataset.catalogueItem = device.id;
          item.dataset.searchName = device.name.toLocaleLowerCase(locale);
          item.dataset.sourceIndex = String(device.sourceIndex ?? 0);
          item.dataset.applianceCategory = getDeviceGroupId(device);
          const catalogueCard = createDeviceCard(device, true);
          // Preview the selected appearance only; hover never changes usage/state.
          catalogueCard.addEventListener('pointerenter', event => {
            if (event.pointerType !== 'touch') catalogueCard.classList.add('is-selection-preview');
          });
          catalogueCard.addEventListener('pointerleave', () => {
            if (!catalogueCard.classList.contains('is-drag-source')) catalogueCard.classList.remove('is-selection-preview');
          });
          catalogueCard.addEventListener('pointerdown', () => catalogueCard.classList.add('is-selection-preview'));
          ['pointerup', 'pointercancel'].forEach(type => catalogueCard.addEventListener(type, event => {
            if (event.pointerType === 'touch') catalogueCard.classList.remove('is-selection-preview');
          }));
          const selectionButton = catalogueCard.querySelector('[data-device-select]');
          item.appendChild(catalogueCard);
          item.addEventListener('click', event => {
            if (event.target.closest('button, input, label')) return;
            selectionButton.click();
          });
          setupApplianceDrag(item, catalogueCard, () => { if (!device.active) selectionButton.click(); });
          list.appendChild(item);
        }
      });
      [...list.children].sort((a, b) => Number(a.dataset.sourceIndex) - Number(b.dataset.sourceIndex)).forEach(item => list.appendChild(item));
      applyCatalogueFilter();
      if (focusedCatalogueId) {
        const focusSelector = focused?.dataset.field ? `[data-field="${focused.dataset.field}"]`
          : focused?.hasAttribute('data-device-simultaneous') ? '[data-device-simultaneous]'
          : focused?.hasAttribute('data-consumption-help') ? '[data-consumption-help]' : '[data-device-select]';
        const focusTarget = catalogue.querySelector(`[data-catalogue-item="${focusedCatalogueId}"] ${focusSelector}`)
          || selectedSection.querySelector(`[data-device-id="${focusedCatalogueId}"] ${focusSelector}`);
        focusTarget?.focus({ preventScroll: true });
      }

      window.requestAnimationFrame(() => {
        deviceGrid.querySelectorAll('.w21-power-calculator__device-name-input').forEach(fitCustomNameInput);
      });
    };

    const tripEnergyLabels = {
      en: { consumption: 'Total consumption', solar: 'Expected solar input' },
      nl: { consumption: 'Totaal verbruik', solar: 'Verwachte zonne-opbrengst' },
      de: { consumption: 'Gesamtverbrauch', solar: 'Erwarteter Solarertrag' }
    }[locale];
    const getTripEnergy = totals => {
      const days = clamp(Number(root.querySelector('[data-trip-days-number]')?.value) || 1, 0.5, 14);
      // Display period totals without changing the daily balance used by advice.
      return { days, consumptionWh: totals.dailyWh * days, solarWh: totals.solarWh * days };
    };

    const renderStatus = () => {
      if (ensureRecommendedSetup()) { renderProducts(); }
      const station = getSelectedPowerStation();
      const totals = getTotals();
      const period = getTripEnergy(totals);

      if (stationOutput) stationOutput.textContent = station ? `${formatNumber(station.output)} W` : labels.selectSetup;
      if (appliancesOutput) appliancesOutput.textContent = `${formatNumber(totals.peakW)} W`;
      if (outputStatus) outputStatus.textContent = station
        ? (totals.outputRemaining >= 0 ? `${formatNumber(totals.outputRemaining)} W ${labels.outputLeft}` : `${labels.outputExceeded} ${formatNumber(Math.abs(totals.outputRemaining))} W`)
        : labels.outputEmpty;

      if (capacityTotal) capacityTotal.textContent = station ? formatNumber(totals.availableWh) + ' Wh' : labels.selectSetup;
      if (capacityStatus) capacityStatus.textContent = !station ? labels.capacityEmpty : totals.capacityRemaining >= 0
        ? formatNumber(totals.capacityRemaining) + ' Wh ' + labels.capacityLeft
        : labels.capacityShort + ' ' + formatNumber(Math.abs(totals.capacityRemaining)) + ' Wh';
      if (!resultStacks.length) return;
      const hasAppliances = state.devices.some(device => device.active);
      const hasSolar = Boolean(state.selectedSolarId && state.solarCount > 0);
      const barLabels = {
        en: { appliances: tripEnergyLabels.consumption, capacity: 'Battery', solar: tripEnergyLabels.solar },
        nl: { appliances: tripEnergyLabels.consumption, capacity: 'Accu', solar: tripEnergyLabels.solar },
        de: { appliances: tripEnergyLabels.consumption, capacity: 'Akku', solar: tripEnergyLabels.solar }
      }[locale];
      const days = period.days;
      const powerNeeded = totals.peakW;
      const recommendedChoice = hasAppliances
        ? getRecommendedChoice(totals.dailyWh, powerNeeded, days) : null;
      // Keep the reserve for normal advice. Only the actual maximum fallback
      // may use the full battery when no available setup meets the reserve.
      const reserveRatio = station && !recommendedChoice &&
        sameSetup(getSetupSelection(), getRecommendedSelection(getMaximumChoice(totals.dailyWh, powerNeeded, days))) ? 0 : 0.2;
      const insufficient = Boolean(station && hasAppliances &&
        !coversTrip(totals.batteryCapacity, totals.dailyWh, totals.solarWh, days, station.capacity, reserveRatio));
      const supply = [
        { type: 'battery', label: barLabels.capacity, value: hasAppliances && station ? totals.batteryCapacity : 0 },
        ...(hasAppliances && hasSolar ? [{ type: 'solar', label: barLabels.solar, value: period.solarWh }] : [])
      ];
      const supplyWh = supply.reduce((sum, segment) => sum + segment.value, 0);
      const scale = Math.max(1, period.consumptionWh, supplyWh);
      const consumptionWh = hasAppliances ? period.consumptionWh : 0;
      const consumption = `
        <div class="w21-power-calculator__segmented-capacity-group is-appliances${insufficient ? ' is-shortfall' : ''}" data-energy-bar="appliances" data-wh="${period.consumptionWh}" data-daily-wh="${totals.dailyWh}" data-energy-days="${days}">
          <div class="w21-power-calculator__segmented-capacity-labels"><div class="w21-power-calculator__segmented-capacity-label"><span>${barLabels.appliances}</span></div></div>
          <div class="w21-power-calculator__segmented-capacity-bar"><div class="w21-power-calculator__segmented-capacity-segment"><span class="w21-power-calculator__segmented-capacity-fill" style="width: ${consumptionWh / scale * 100}%"></span></div></div>
          <div class="w21-power-calculator__segmented-capacity-values"><div class="w21-power-calculator__segmented-capacity-value"><strong>${hasAppliances ? `${formatNumber(consumptionWh)} Wh` : '–'}</strong></div></div>
        </div>`;
      // Empty supply still reserves its label, track and value row.
      const supplyWidth = supplyWh > 0 ? supplyWh / scale * 100 : 100;
      const segmentSize = segment => Math.max(segment.value, 1);
      const supplyBar = supply.length ? `
        <div class="w21-power-calculator__segmented-capacity-group is-supply" data-energy-supply>
          <div class="w21-power-calculator__segmented-capacity-labels" style="width: ${supplyWidth}%">${supply.map(segment => `
            <div class="w21-power-calculator__segmented-capacity-label" style="--w21-segment-size: ${segmentSize(segment)}"><span>${segment.label}</span></div>`).join('')}</div>
          <div class="w21-power-calculator__segmented-capacity-bar">
            <div class="w21-power-calculator__supply-parts" style="width: ${supplyWidth}%">${supply.map(segment => `
                <div class="w21-power-calculator__segmented-capacity-segment${segment.type === 'solar' ? ' is-solar' : ''}" data-energy-bar="${segment.type}" data-wh="${segment.value}"${segment.type === 'solar' ? ` data-daily-wh="${totals.solarWh}" data-energy-days="${days}"` : ''} style="--w21-segment-size: ${segmentSize(segment)}"><span class="w21-power-calculator__segmented-capacity-fill" style="width: ${segment.value > 0 ? 100 : 0}%"></span></div>`).join('')}
            </div>
          </div>
          <div class="w21-power-calculator__segmented-capacity-values" style="width: ${supplyWidth}%">${supply.map(segment => `
            <div class="w21-power-calculator__segmented-capacity-value" style="--w21-segment-size: ${segmentSize(segment)}"><strong>${segment.value > 0 ? `${formatNumber(segment.value)} Wh` : '–'}</strong></div>`).join('')}</div>
        </div>` : '';
      let conclusion = `<div class="w21-power-calculator__energy-status is-neutral" data-setup-status="neutral" data-setup-message="empty" role="status"><span class="w21-power-calculator__setup-plus w21-power-calculator__energy-status-icon" aria-hidden="true">${minusIcon}</span><div class="w21-power-calculator__energy-status-text"><span>–</span></div></div>`;
      const suggestedSelection = currentRecommendation ? getRecommendedSelection(currentRecommendation) : null;
      // The entire result must describe one setup. Manual choices stay visible;
      // the independently calculated recommendation is only adopted by Switch.
      const visibleSelection = selectionIsManual ? getSetupSelection() : suggestedSelection;
      const visibleItems = hasAppliances && visibleSelection ? getSelectedSetupItems(visibleSelection) : [];
      const powerShort = Boolean(station && powerNeeded > station.output);
      const needsSetup = hasAppliances && (!station || insufficient || powerShort);
      const previewTitle = selectionIsManual ? labels.yourSelection
        : { en: 'Recommended setup', nl: 'Aanbevolen setup', de: 'Empfohlenes Setup' }[locale];
      const visibleCards = visibleItems.length ? `<div data-summary-recommendation data-results-setup="${selectionIsManual ? 'manual' : 'automatic'}" role="group" aria-label="${escapeHtml(previewTitle)}">${renderCombination(visibleItems, true)}</div>` : '';
      const differs = Boolean(suggestedSelection && !sameSetup(getSetupSelection(), suggestedSelection));
      if (hasAppliances) {
        let messageKey = 'works';
        let statusKind = needsSetup ? 'error' : 'success';
        if (!station) messageKey = 'selectionEmpty';
        else if (!recommendedChoice && needsSetup) {
          messageKey = powerShort && insufficient ? 'maximumBoth' : powerShort ? 'maximumOutput'
            : root.querySelector('[data-solar-advice]')?.getAttribute('aria-checked') === 'true' ? 'maximumCapacity' : 'maximumBattery';
        } else if (needsSetup) {
          messageKey = powerShort && insufficient ? 'moreBoth' : powerShort ? 'moreOutput' : 'moreCapacity';
        } else if (differs) {
          const lessCapacity = currentRecommendation.capacity < totals.batteryCapacity;
          const lessOutput = currentRecommendation.station.output < station.output;
          const solarPreferred = currentRecommendation.solar && currentRecommendation.expansionCount < state.expansionCount;
          messageKey = solarPreferred ? 'solar' : lessCapacity && lessOutput ? 'lessBoth' : lessCapacity ? 'lessCapacity' : lessOutput ? 'lessOutput' : 'works';
          statusKind = messageKey === 'works' ? 'success' : 'advice';
        }
        const crossIcon = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15"/></svg>';
        const switchIcon = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M15.5 6.5A6 6 0 0 0 5 5l-1.5 1.5M4.5 13.5A6 6 0 0 0 15 15l1.5-1.5M3.5 3.5v3h3M16.5 16.5v-3h-3"/></svg>';
        const message = messageKey === 'selectionEmpty' ? labels.selectionEmpty : setupConclusionCopy[messageKey];
        const canSwitch = differs && messageKey !== 'works';
        const adviceStart = message.search(/ (?:Switch to |Schakel over naar |Wechsle zu )/);
        const actionText = canSwitch && adviceStart >= 0 ? message.slice(adviceStart + 1) : '';
        const leadingIcon = canSwitch
          ? `<button type="button" class="w21-power-calculator__suggested-switch" data-summary-switch aria-label="${escapeHtml(actionText || labels.switchSuggested)}" title="${escapeHtml(actionText || labels.switchSuggested)}"><span class="w21-power-calculator__setup-plus w21-power-calculator__energy-status-icon" aria-hidden="true">${switchIcon}</span></button>`
          : `<span class="w21-power-calculator__setup-plus w21-power-calculator__energy-status-icon" aria-hidden="true">${needsSetup ? crossIcon : checkIcon}</span>`;
        conclusion = `<div class="w21-power-calculator__energy-status is-${statusKind}" data-setup-status="${statusKind}" data-setup-message="${messageKey}" role="status">${leadingIcon}<div class="w21-power-calculator__energy-status-text"><span>${escapeHtml(message)}</span></div></div>`;
      }
      if (summaryConclusion) { summaryConclusion.innerHTML = conclusion; summaryConclusion.hidden = !conclusion; }
      // Do not replace the controls during calculation: a focused input or
      // dragging range thumb must keep its DOM node and pointer interaction.
      resultStacks.forEach(stack => {
        const settings = resultSettings.get(stack);
        const metrics = stack.querySelector('[data-results], [data-step-results]');
        [...stack.children].forEach(child => { if (child !== settings && child !== metrics && child !== summaryConclusion) child.remove(); });
        metrics.insertAdjacentHTML('beforebegin', (stack === capacityStack ? '' : conclusion) + consumption + supplyBar);
        stack.insertAdjacentHTML('beforeend', visibleCards);
        const consumptionValues = stack.querySelector('.is-appliances .w21-power-calculator__segmented-capacity-values');
        if (consumptionValues) consumptionValues.style.width = `${consumptionWh > 0 ? consumptionWh / scale * 100 : 100}%`;
      });
      // Duration and appliance edits also update profile visibility without
      // rebuilding either focused input or the result controls.
      renderSolarRegions();
    };

    // Both views delegate to persistent containers and use the latest selection.
    resultStacks.forEach(stack => stack.addEventListener('click', event => {
      const productToggle = event.target.closest('[data-result-product-toggle]');
      if (productToggle && stack.contains(productToggle)) {
        event.preventDefault();
        const productId = productToggle.dataset.resultProductToggle;
        if (productId === state.selectedPowerStationId) {
          state.selectedPowerStationId = '';
          state.expansionCount = 0;
          state.selectedSolarId = '';
          state.solarCount = 0;
        } else if (productId === 'expansion' && state.expansionCount > 0) {
          state.expansionCount = 0;
        } else if (productId === state.selectedSolarId && state.solarCount > 0) {
          state.selectedSolarId = '';
          state.solarCount = 0;
          rememberedSolarSelection = null;
        } else return;
        const restoreFocus = document.activeElement === productToggle;
        selectionIsManual = true;
        renderAll();
        if (restoreFocus) {
          const next = stack.querySelector('[data-result-product-toggle], [data-summary-switch], [data-setup-status]');
          if (next) { if (next.hasAttribute('data-setup-status')) next.tabIndex = -1; next.focus({ preventScroll: true }); }
        }
        return;
      }
      const quantityButton = event.target.closest('[data-summary-quantity]');
      if (quantityButton && stack.contains(quantityButton)) {
        if (quantityButton.disabled || !quantityButton.closest('[data-summary-recommendation]')) return;
        event.preventDefault();
        const kind = quantityButton.dataset.summaryQuantity;
        const direction = Number(quantityButton.dataset.summaryDirection);
        if (changeSetupQuantity(kind, direction, quantityButton.dataset.summaryPanel)) {
          const nextControls = [...stack.querySelectorAll('[data-summary-quantity]')].filter(control =>
            control.dataset.summaryQuantity === kind && !control.disabled);
          (nextControls.find(control => Number(control.dataset.summaryDirection) === direction) || nextControls[0])?.focus({ preventScroll: true });
        }
        return;
      }
    }));
    root.addEventListener('click', event => {
      const action = event.target.closest('[data-summary-switch]');
      if (!action || !root.contains(action) || !getSuggestedChoice()) return;
      event.preventDefault();
      selectionIsManual = false;
      const fromSummary = action.closest('[data-summary-conclusion]');
      renderAll();
      const status = (fromSummary ? summaryConclusion : root.querySelector('[data-step-results-stack]'))?.querySelector('[data-setup-status]');
      if (status) { status.tabIndex = -1; status.focus({ preventScroll: true }); }
    });

    const coversTrip = (capacity, dailyWh, solarWh, days, mainUnitCapacity, reserveRatio = 0.2) => {
      let remaining = capacity;
      for (let day = 0; day < days; day += 1) {
        // Daily balance: generation can offset use, but surplus cannot exceed storage.
        const fraction = Math.min(1, days - day);
        remaining = Math.min(capacity, remaining + (solarWh - dailyWh) * fraction);
        if (remaining + 1e-9 < mainUnitCapacity * reserveRatio) return false;
      }
      return true;
    };

    const revealSelectedSolar = () => {
      if (!state.selectedSolarId || state.solarCount <= 0) return;
      if (solarOptions) solarOptions.hidden = false;
      if (mobileResultsMedia.matches) root.classList.add('is-solar-picker-open');
      solarDisclosure?.setAttribute('aria-expanded', 'true');
      if (solarDisclosureLabel) solarDisclosureLabel.textContent = labels.hideSolarPanels;
    };

    const getRecommendedChoice = (dailyWh, outputNeeded, days) => {
      const wantsSolar = root.querySelector('[data-solar-advice]')?.getAttribute('aria-checked') === 'true';
      const candidates = powerStations.flatMap((station) => {
        if (station.output < outputNeeded) return [];
        const maxExpansion = station.compatibleExpansion ? expansionBattery.maxQuantity : 0;
        return Array.from({ length: maxExpansion + 1 }, (_, expansionCount) => {
          const capacity = station.capacity + expansionCount * expansionBattery.capacity;
          const batterySufficient = coversTrip(capacity, dailyWh, 0, days, station.capacity);
          const solarChoices = wantsSolar && !batterySufficient ? solarPanels.flatMap((panel) => {
            const max = solarMaxByStation[station.id]?.[panel.id] || 0;
            return Array.from({ length: max }, (_, index) => ({ panel, quantity: index + 1, input: panel.watts * state.solarSunHours * (index + 1) }));
          }) : [];
          const solar = solarChoices.filter((item) => coversTrip(capacity, dailyWh, item.input, days, station.capacity)).sort((a, b) => a.input - b.input || a.quantity - b.quantity)[0] || null;
          return { station, expansionCount, capacity, solar, sufficient: batterySufficient || solar !== null };
        });
      });
      // Prefer the smallest sufficient main unit without expansion batteries.
      // With solar enabled, a smaller unit with panels outranks a larger bare
      // unit. Panels are only needed when that unit cannot cover the trip alone.
      const rank = candidate => candidate.expansionCount === 0 ? 0
        : wantsSolar && candidate.solar ? 1 : 2;
      return candidates.filter(candidate => candidate.sufficient).sort((a, b) =>
        rank(a) - rank(b) || a.capacity - b.capacity || a.expansionCount - b.expansionCount || a.station.output - b.station.output
      )[0];

    };

    const setupConclusionCopy = {
      en: {
        works: 'Your current setup works.',
        moreCapacity: 'Your current setup needs more capacity.',
        moreOutput: 'Your current setup needs more output power.',
        moreBoth: 'Your current setup needs more capacity and output power.',
        lessCapacity: 'Your current setup has excess capacity. Switch to a lower-capacity setup.',
        lessOutput: 'Your current setup has excess output power. Switch to a lower-output setup.',
        lessBoth: 'Your current setup has excess capacity and output power. Switch to a smaller setup.',
        solar: 'Your current setup works. Switch to solar power with fewer extra batteries.',
        maximumOutput: 'Maximum output reached. Reduce appliance power or simultaneous use.',
        maximumBattery: 'Maximum battery capacity reached. Enable solar power or reduce energy usage.',
        maximumCapacity: 'Maximum capacity reached. Reduce energy usage or off-grid days.',
        maximumBoth: 'Maximum setup reached. Reduce energy usage and power demand.'
      },
      nl: {
        works: 'Je huidige setup voldoet.',
        moreCapacity: 'Je huidige setup heeft meer capaciteit nodig.',
        moreOutput: 'Je huidige setup heeft meer uitgangsvermogen nodig.',
        moreBoth: 'Je huidige setup heeft meer capaciteit en uitgangsvermogen nodig.',
        lessCapacity: 'Je huidige setup heeft overtollige capaciteit. Schakel over naar een setup met minder capaciteit.',
        lessOutput: 'Je huidige setup heeft overtollig uitgangsvermogen. Schakel over naar een setup met minder vermogen.',
        lessBoth: 'Je huidige setup heeft overtollige capaciteit en uitgangsvermogen. Schakel over naar een kleinere setup.',
        solar: 'Je huidige setup voldoet. Schakel over naar zonne-energie met minder uitbreidingsaccu’s.',
        maximumOutput: 'Maximaal uitgangsvermogen bereikt. Verlaag apparaatvermogen of gelijktijdig gebruik.',
        maximumBattery: 'Maximale accucapaciteit bereikt. Schakel zonne-energie in of verlaag het energieverbruik.',
        maximumCapacity: 'Maximale capaciteit bereikt. Verlaag het energieverbruik of het aantal off-grid dagen.',
        maximumBoth: 'Maximale setup bereikt. Verlaag het energieverbruik en het benodigde vermogen.'
      },
      de: {
        works: 'Dein aktuelles Setup reicht aus.',
        moreCapacity: 'Dein aktuelles Setup benötigt mehr Kapazität.',
        moreOutput: 'Dein aktuelles Setup benötigt mehr Ausgangsleistung.',
        moreBoth: 'Dein aktuelles Setup benötigt mehr Kapazität und Ausgangsleistung.',
        lessCapacity: 'Dein aktuelles Setup hat überschüssige Kapazität. Wechsle zu einem Setup mit weniger Kapazität.',
        lessOutput: 'Dein aktuelles Setup hat überschüssige Ausgangsleistung. Wechsle zu einem Setup mit weniger Leistung.',
        lessBoth: 'Dein aktuelles Setup hat überschüssige Kapazität und Ausgangsleistung. Wechsle zu einem kleineren Setup.',
        solar: 'Dein aktuelles Setup reicht aus. Wechsle zu Solarenergie mit weniger Zusatzakkus.',
        maximumOutput: 'Maximale Ausgangsleistung erreicht. Reduziere Geräteleistung oder gleichzeitige Nutzung.',
        maximumBattery: 'Maximale Akkukapazität erreicht. Aktiviere Solarenergie oder reduziere den Energieverbrauch.',
        maximumCapacity: 'Maximale Kapazität erreicht. Reduziere den Energieverbrauch oder die Off-grid-Tage.',
        maximumBoth: 'Maximales Setup erreicht. Reduziere Energieverbrauch und Leistungsbedarf.'
      }
    }[locale];
    const getMaximumChoice = (dailyWh, outputNeeded, days) => {
      // Output is a hard constraint: expansion batteries cannot raise it.
      const station = powerStations.find(item => item.id === (outputNeeded > 1800 ? 'move_2000' : 'move_1800'));
      const expansionCount = station.compatibleExpansion ? expansionBattery.maxQuantity : 0;
      const capacity = station.capacity + expansionCount * expansionBattery.capacity;
      const wantsSolar = root.querySelector('[data-solar-advice]')?.getAttribute('aria-checked') === 'true';
      const solar = wantsSolar && !coversTrip(capacity, dailyWh, 0, days, station.capacity)
        ? solarPanels.map(panel => ({ panel, quantity: solarMaxByStation[station.id]?.[panel.id] || 0 }))
          .filter(item => item.quantity > 0).sort((a, b) => b.panel.watts * b.quantity - a.panel.watts * a.quantity)[0] : null;
      return { station, expansionCount, capacity, solar };
    };
    const getSuggestedChoice = () => {
      if (!state.devices.some(device => device.active)) return null;
      const totals = getTotals();
      const days = getTripEnergy(totals).days;
      return getRecommendedChoice(totals.dailyWh, totals.peakW, days)
        || getMaximumChoice(totals.dailyWh, totals.peakW, days);
    };
    const changeSetupQuantity = (kind, direction, panelId) => {
      const station = getSelectedPowerStation();
      if (!station || ![-1, 1].includes(direction)) return false;
      const previous = getSetupSelection();
      if (kind === 'expansion') {
        if (!station.compatibleExpansion) return false;
        state.expansionCount = clamp(state.expansionCount + direction, 0, expansionBattery.maxQuantity);
      } else if (kind === 'solar') {
        const maximum = getSolarMax(panelId);
        if (!maximum || (direction < 0 && state.selectedSolarId !== panelId)) return false;
        const quantity = state.selectedSolarId === panelId ? state.solarCount : 0;
        state.solarCount = clamp(quantity + direction, 0, maximum);
        state.selectedSolarId = state.solarCount > 0 ? panelId : '';
        if (state.solarCount === 0) rememberedSolarSelection = null;
        else syncSolarAdvice(true);
      } else return false;
      if (sameSetup(previous, getSetupSelection())) return false;
      selectionIsManual = true;
      renderAll();
      return true;
    };
    const ensureRecommendedSetup = () => {
      currentRecommendation = getSuggestedChoice();
      if (selectionIsManual) return false;
      const selection = currentRecommendation ? getRecommendedSelection(currentRecommendation)
        : { selectedPowerStationId: '', expansionCount: 0, selectedSolarId: '', solarCount: 0 };
      if (sameSetup(getSetupSelection(), selection)) return false;
      rememberSolarSelection();
      Object.assign(state, selection);
      cleanupCompatibility();
      rememberSolarSelection();
      revealSelectedSolar();
      return true;
    };

    const renderCombination = (items, editable = false) => `<div class="w21-power-calculator__setup-row">${items.map(({ product, quantity }) => {
      const bars = product.id === 'expansion' ? getExpansionBars() : product.watts !== undefined ? getSolarBars(product) : getStationBars(product);
      const isExpansion = product.id === 'expansion';
      const isSolar = product.watts !== undefined;
      const ownStation = getSelectedPowerStation();
      const maxQuantity = isExpansion ? (ownStation?.compatibleExpansion ? expansionBattery.maxQuantity : 0) : solarMaxByStation[ownStation?.id]?.[product.id] || 0;
      const ownQuantity = isExpansion ? state.expansionCount : state.selectedSolarId === product.id ? state.solarCount : 0;
      const selected = isExpansion ? state.expansionCount > 0 : isSolar ? state.selectedSolarId === product.id && state.solarCount > 0 : state.selectedPowerStationId === product.id;
      const toggleLabel = { en: 'Deselect', nl: 'Deselecteer', de: 'Abwählen' }[locale];
      const productCheck = editable
        ? `<button type="button" class="w21-power-calculator__product-check" data-product-check data-result-product-toggle="${escapeHtml(product.id)}" aria-pressed="${selected}" aria-label="${escapeHtml(`${toggleLabel}: ${product.name}`)}">${checkIcon}</button>`
        : `<span class="w21-power-calculator__product-check" data-product-check aria-hidden="true">${checkIcon}</span>`;
      const quantityAttributes = direction => editable
        ? `data-summary-quantity="${isExpansion ? 'expansion' : 'solar'}" data-summary-direction="${direction}"${isSolar ? ` data-summary-panel="${escapeHtml(product.id)}"` : ''}`
        : '';
      const controls = isSolar || isExpansion ? `<span class="w21-power-calculator__${isExpansion ? 'expansion' : 'solar'}-controls"><button type="button" class="w21-power-calculator__quantity-button" ${quantityAttributes(-1)} ${!editable || !maxQuantity || ownQuantity <= 0 ? 'disabled' : ''} aria-label="${isExpansion ? labels.decreaseExpansion : labels.decreaseSolar}">${minusIcon}</button><strong>${ownQuantity} / ${maxQuantity}</strong><button type="button" class="w21-power-calculator__quantity-button" ${quantityAttributes(1)} ${!editable || !maxQuantity || ownQuantity >= maxQuantity ? 'disabled' : ''} aria-label="${isExpansion ? labels.increaseExpansion : labels.increaseSolar}">${plusIcon}</button></span>` : '';
      return `<div class="w21-power-calculator__product-card w21-power-calculator__setup-product${selected ? ' is-active' : ''}" data-suggested-product="${product.id}" data-suggested-count="${quantity}"><span class="w21-power-calculator__product-media">${getProductImageHtml(product)}</span><span class="w21-power-calculator__product-content"><span class="w21-power-calculator__product-head"><strong class="w21-power-calculator__product-title">${escapeHtml(product.name)}</strong>${productCheck}</span>${bars}${controls}</span></div>`;
    }).join(`<span class="w21-power-calculator__setup-plus" aria-hidden="true">${plusIcon}</span>`)}</div>`;

    const solarAdviceButtons = [...root.querySelectorAll('[data-solar-advice], [data-summary-solar-advice]')];
    const syncSolarAdvice = checked => {
      solarAdviceButtons.forEach(control => {
        control.setAttribute('aria-checked', String(checked));
        const label = control.closest('.w21-power-calculator__solar-advice-option')?.querySelector('[data-i18n="solarAdvice"]');
        if (label) label.textContent = checked ? labels.disableSolarPanels : labels.enableSolarPanels;
      });
    };
    syncSolarAdvice(root.querySelector('[data-solar-advice]')?.getAttribute('aria-checked') === 'true');
    solarAdviceButtons.forEach(button => button.addEventListener('click', () => {
      const checked = button.getAttribute('aria-checked') !== 'true';
      syncSolarAdvice(checked);
      if (!checked) {
        rememberSolarSelection();
        state.selectedSolarId = '';
        state.solarCount = 0;
        // A previous recommendation must not restore solar after opting out.
        root.classList.remove('is-solar-picker-open');
      } else if (rememberedSolarSelection) {
        const max = getSolarMax(rememberedSolarSelection.id);
        if (max > 0) {
          state.selectedSolarId = rememberedSolarSelection.id;
          state.solarCount = Math.min(rememberedSolarSelection.count, max);
          revealSelectedSolar();
        }
      }
      renderAll();
    }));

    const tripNumber = root.querySelector('[data-trip-days-number]');
    const tripSliders = [...root.querySelectorAll('[data-trip-days], [data-summary-trip-days]')];
    const tripNumbers = [...root.querySelectorAll('[data-trip-days-number], [data-summary-trip-days-number]')];
    const daysToSliderPosition = (days) => days <= 7 ? (days - 0.5) / 6.5 * 70 : 70 + (days - 7) / 7 * 30;
    const sliderPositionToDays = (position) => position <= 70 ? 0.5 + position / 70 * 6.5 : 7 + (position - 70) / 30 * 7;
    let syncedTripDays = null;
    const syncTripDays = (rawValue, render = true) => {
      const numericValue = Number(String(rawValue).replace(',', '.'));
      const boundedValue = Number.isFinite(numericValue) && String(rawValue).trim() !== '' ? numericValue : 1;
      const days = clamp(Math.round(boundedValue * 2) / 2, 0.5, 14);
      const changed = days !== syncedTripDays;
      syncedTripDays = days;
      tripSliders.forEach(slider => {
        slider.value = String(daysToSliderPosition(days));
        slider.setAttribute('aria-valuetext', formatNumber(days, 1) + ' ' + labels.daysUnit);
      });
      tripNumbers.forEach(number => { number.value = String(days); });
      // Recalculate advice without replacing the focused duration controls.
      if (render && changed) {
          renderStatus(); renderResults();
      }
    };
    syncTripDays(tripNumber?.value || 1, false);
    tripNumbers.forEach(number => {
      number.addEventListener('input', () => { if (number.value !== '') syncTripDays(number.value); });
      number.addEventListener('change', () => syncTripDays(number.value));
    });
    tripSliders.forEach(slider => {
      slider.addEventListener('input', () => syncTripDays(sliderPositionToDays(Number(slider.value))));
      slider.addEventListener('keydown', (event) => {
        slider.classList.add('is-interacting');
        const current = Number(tripNumber?.value) || 1;
        const next = { ArrowRight: current + 0.5, ArrowUp: current + 0.5, ArrowLeft: current - 0.5, ArrowDown: current - 0.5, Home: 0.5, End: 14, PageUp: current + 1, PageDown: current - 1 }[event.key];
        if (next !== undefined) {
          event.preventDefault();
          syncTripDays(next);
        }
      });
      slider.addEventListener('keyup', () => slider.classList.remove('is-interacting'));
      slider.addEventListener('blur', () => slider.classList.remove('is-interacting'));
    });

    const renderResults = () => {
      if (!results) return;
      const totals = getTotals();
      const period = getTripEnergy(totals);

      const groupNames = {
        en: { setup: 'Selected Setup', needs: 'Appliances' },
        nl: { setup: 'Geselecteerde Setup', needs: 'Apparaten' },
        de: { setup: 'Ausgewähltes Setup', needs: 'Geräte' }
      }[locale];
      const metrics = [
        [labels.output, totals.outputW, 'W', labels.output],
        [labels.batteryCapacity, totals.batteryCapacity, 'Wh', labels.shortCapacity],
        [tripEnergyLabels.solar, period.solarWh, 'Wh', tripEnergyLabels.solar],
        [labels.peakPower, totals.peakW, 'W', labels.peakPower],
        [tripEnergyLabels.consumption, period.consumptionWh, 'Wh', tripEnergyLabels.consumption]
      ];
      const hasStation = Boolean(getSelectedPowerStation());
      const hasAppliances = state.devices.some((device) => device.active);
      const hasSolar = Boolean(state.selectedSolarId && state.solarCount > 0);
      const setupMetrics = hasSolar ? metrics.slice(0, 3) : metrics.slice(0, 2);
      resultMetrics.forEach(metrics => { metrics.style.gridTemplateColumns = '2fr ' + setupMetrics.length + 'fr'; });
      const helpText = {
        en: 'Total consumption shows the estimated battery energy your appliance(s) need over your selected number of days. We multiply daily use by the selected duration and include estimated energy losses.',
        nl: 'Totaal verbruik toont de geschatte accu-energie die je apparaten nodig hebben voor het gekozen aantal dagen. We vermenigvuldigen het dagelijks verbruik met de gekozen duur en nemen geschatte energieverliezen mee.',
        de: 'Gesamtverbrauch zeigt die geschätzte Akkuenergie deiner Geräte für die gewählte Anzahl an Tagen. Wir multiplizieren den täglichen Verbrauch mit der gewählten Dauer und berücksichtigen geschätzte Energieverluste.'
      }[locale];
      const consumptionHelp = `<button type="button" class="w21-power-calculator__consumption-help" data-consumption-help data-help-text="${escapeHtml(helpText)}" aria-label="${escapeHtml(tripEnergyLabels.consumption)} info" aria-haspopup="dialog"><svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor"/><g transform="translate(2 2) scale(.8)"><path d="M7.5 7a2.5 2.5 0 0 1 5 .2c0 1.8-2.5 1.8-2.5 3.6" fill="none" stroke="#fff" stroke-width="1.7" stroke-linecap="round"/><circle cx="10" cy="14.3" r="1" fill="#fff"/></g></svg></button>`;
      const metricHelp = {
        en: {
          appliancePower: 'Power is the higher of the combined wattage of appliances marked for simultaneous use in step 1 or the highest wattage of any selected appliance. Switching simultaneous use off does not remove an appliance or its energy consumption.',
          stationPower: 'Power shows the rated output of your selected power station. Appliance(s) used at the same time must stay within this limit. Adding expansion batteries or solar panels does not increase the output.',
          battery: 'Battery shows the total capacity of your power station and expansion batteries. Normal advice keeps a 20% main-unit reserve. Only the maximum available setup may use full capacity if no setup meets this reserve. Solar input is shown separately.',
          solar: 'Expected solar input shows expected generation over your selected number of days. Usable solar energy depends on daily consumption and battery storage; surplus generation cannot exceed available storage.'
        },
        nl: {
          appliancePower: 'Vermogen is de hoogste waarde van het gezamenlijke vermogen voor gelijktijdig gebruik in stap 1 of het vermogen van het zwaarste geselecteerde apparaat. Gelijktijdig gebruik uitschakelen verwijdert het apparaat of zijn energieverbruik niet.',
          stationPower: 'Vermogen toont het nominale uitgangsvermogen van je powerstation. Apparaten die tegelijk werken moeten binnen deze grens blijven. Uitbreidingsaccu’s en zonnepanelen verhogen dit vermogen niet.',
          battery: 'Accu toont de totale capaciteit van je powerstation en uitbreidingsaccu’s. Normaal advies houdt 20% van de hoofdunit als reserve. Alleen de maximaal beschikbare setup mag de volledige capaciteit gebruiken als geen setup met reserve voldoet. Zonne-opbrengst staat apart.',
          solar: 'Verwachte zonne-opbrengst toont de verwachte opwekking voor het gekozen aantal dagen. Bruikbare zonne-energie hangt af van dagelijks verbruik en accuopslag; een overschot kan de beschikbare opslag niet overschrijden.'
        },
        de: {
          appliancePower: 'Leistung ist der höhere Wert aus der Gesamtleistung der in Schritt 1 gleichzeitig genutzten Geräte und der Leistung des stärksten ausgewählten Geräts. Das Ausschalten der gleichzeitigen Nutzung entfernt weder das Gerät noch seinen Energieverbrauch.',
          stationPower: 'Leistung zeigt die Nennleistung deiner Powerstation. Gleichzeitig verwendete Geräte müssen innerhalb dieser Grenze bleiben. Zusatzakkus und Solarmodule erhöhen die Ausgangsleistung nicht.',
          battery: 'Akku zeigt die Gesamtkapazität deiner Powerstation und Zusatzakkus. Normale Empfehlungen halten 20% der Haupteinheit als Reserve. Nur das maximal verfügbare Setup darf die volle Kapazität nutzen, wenn kein Setup mit Reserve ausreicht. Solarertrag wird separat angezeigt.',
          solar: 'Erwarteter Solarertrag zeigt die erwartete Erzeugung für die gewählte Anzahl an Tagen. Nutzbare Solarenergie hängt vom täglichen Verbrauch und Akkuspeicher ab; Überschüsse können den verfügbaren Speicher nicht überschreiten.'
        }
      }[locale];
      const makeMetricHelp = (title, text) => consumptionHelp
        .replace('data-consumption-help', `data-consumption-help data-help-title="${escapeHtml(title)}"`)
        .replace(`data-help-text="${escapeHtml(helpText)}"`, `data-help-text="${escapeHtml(text)}"`)
        .replace(`aria-label="${escapeHtml(tripEnergyLabels.consumption)} info"`, `aria-label="${escapeHtml(title)} info"`);
      const metricCard = ([label, value, unit, shortLabel], index, hasData, group) => `
        <div class="w21-power-calculator__result-card">
          <span data-short-label="${shortLabel}">${label}${group === 'needs' ? makeMetricHelp(label, index === 0 ? metricHelp.appliancePower : helpText) : makeMetricHelp(label, index === 0 ? metricHelp.stationPower : index === 1 ? metricHelp.battery : metricHelp.solar)}</span>
          <strong>${hasData ? formatNumber(value) + ' ' + unit : '–'}</strong>
        </div>`;
      const markup = `
        <div class="w21-power-calculator__result-section">
          <div class="w21-power-calculator__result-group-heading">${groupNames.needs}</div>
          <div class="w21-power-calculator__result-group w21-power-calculator__result-group--needs">
          ${metrics.slice(3).map((metric, index) => metricCard(metric, index, hasAppliances, 'needs')).join('')}
          </div>
        </div>
        <div class="w21-power-calculator__result-section">
          <div class="w21-power-calculator__result-group-heading">${groupNames.setup}</div>
          <div class="w21-power-calculator__result-group" style="--metric-count: ${setupMetrics.length}">
          ${setupMetrics.map((metric, index) => metricCard(metric, index, hasStation)).join('')}
          </div>
        </div>
      `;
      resultMetrics.forEach(metrics => { metrics.innerHTML = markup; });
    };

    function addCustomDevice() {
      markCustomScenario();
      const usedNumbers = new Set(state.devices.filter(isCustomDevice).map((device) => Number(device.customNumber) || Number((device.defaultName || device.id).match(/(\d+)$/)?.[1])));
      let customNumber = 1;
      while (usedNumbers.has(customNumber)) customNumber += 1;
      state.customCount = customNumber;
      const customId = `custom_${Date.now()}_${customNumber}`;
      const defaultName = `${labels.customDeviceName} ${customNumber}`;
      state.devices.push({ id: customId, isCustom: true, customNumber, name: defaultName, defaultName, nameCustomized: false, usageType: 'timed', watts: 0, hours: 0, minutes: 0, active: true, simultaneous: true, selectionOrder: ++state.selectionSequence });
      renderAll();

      window.requestAnimationFrame(() => {
        const firstCustomInput = deviceGrid?.querySelector(`[data-device-id="${customId}"] .w21-power-calculator__device-name-input`);
        firstCustomInput?.focus();
        firstCustomInput?.select();
      });
    }
    const renderSolarRegions = () => {
      const regionNames = { north: labels.northEurope, south: labels.southEurope, custom: labels.custom };
      solarRegionButtons.forEach((button) => {
        const isActive = button.dataset.solarRegion === state.solarRegion;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        const regionSwitch = button.querySelector('.w21-power-calculator__switch');
        regionSwitch?.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        regionSwitch?.setAttribute('aria-label', `${labels.selectRegion} ${regionNames[button.dataset.solarRegion]}`);
      });
      root.querySelectorAll('[data-sunlight-name]').forEach(el => { el.textContent = regionNames[state.solarRegion] || regionNames.north; });
      root.querySelectorAll('[data-sunlight-hours]').forEach(el => { el.textContent = state.solarSunHours.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }); });
      customSolarHoursInputs.forEach(input => {
        if (input !== document.activeElement) input.value = solarRegions.custom.practicalSunHours.toFixed(1);
      });
      const solarEnabled = root.querySelector('[data-solar-advice]')?.getAttribute('aria-checked') === 'true';
      const solarSelected = Boolean(state.selectedSolarId && state.solarCount > 0);
      if (sunlightTemplate) sunlightTemplate.hidden = !solarSelected;
      const showResultProfile = solarEnabled && (solarSelected || Boolean(currentRecommendation?.solar));
      root.querySelectorAll('[data-results-sunlight]').forEach(profile => { profile.hidden = !showResultProfile; });
    };
    let summaryPositionFrame = 0;
    const updateMobileCartDock = () => {
      if (mobileResultsMedia.matches) {
        calculatorSection.style.setProperty('--w21-mobile-cart-height', '0px');
        return;
      }
      calculatorSection.style.removeProperty('--w21-mobile-cart-height');
    };
    const syncSummaryPosition = () => {
      summaryPositionFrame = 0;
      if (!summaryShell || !summaryPanel) return;

      updateMobileCartDock();
      const summaryHeight = Math.ceil(summaryShell.getBoundingClientRect().height);
      const calculatorIsOpen = calculatorContent ? !calculatorContent.hidden : true;

      calculatorSection.style.setProperty('--w21-summary-height', `${summaryHeight}px`);
      calculatorSection.classList.remove('is-summary-at-end');
      summaryShell.classList.toggle('is-visible', calculatorIsOpen);
      summaryShell.classList.remove('is-at-section-end');
    };
    const queueSummaryPosition = () => {
      if (summaryPositionFrame) return;
      summaryPositionFrame = window.requestAnimationFrame(syncSummaryPosition);
    };
    const renderAll = () => { cleanupCompatibility(); ensureRecommendedSetup(); rememberSolarSelection(); renderSolarRegions(); renderAudienceTabs(); renderProducts(); renderDeviceGrid(); renderStatus(); renderResults(); queueSummaryPosition(); };

    root.querySelectorAll('[data-next-step]').forEach((button) => button.addEventListener('click', () => setStep(state.step + 1)));
    root.querySelectorAll('[data-prev-step]').forEach((button) => button.addEventListener('click', () => setStep(state.step - 1)));
    solarRegionButtons.forEach((button) => button.addEventListener('click', (event) => {
      const region = solarRegions[button.dataset.solarRegion];
      if (!region) return;
      state.solarRegion = button.dataset.solarRegion;
      state.solarSunHours = region.practicalSunHours;
      if (mobileResultsMedia.matches && !event.target.closest('input')) {
        const profile = button.closest('[data-results-sunlight]');
        if (profile) {
          profile.classList.remove('is-profile-open');
          profile.querySelector('[data-sunlight-disclosure]')?.setAttribute('aria-expanded', 'false');
        } else {
          root.classList.remove('is-sunlight-open');
          sunlightDisclosure?.setAttribute('aria-expanded', 'false');
        }
      }
      renderAll();
    }));
    solarRegionButtons.forEach((button) => button.addEventListener('keydown', (event) => {
      if (button.tagName === 'BUTTON' || event.target.closest('input') || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      button.click();
    }));
    customSolarHoursInputs.forEach(input => {
      input.addEventListener('input', () => {
        const practicalSunHours = clamp(Number(input.value.replace(',', '.')) || 0, 0, 12);
        solarRegions.custom.practicalSunHours = practicalSunHours;
        state.solarRegion = 'custom';
        state.solarSunHours = practicalSunHours;
        renderAll();
      });
      input.addEventListener('change', () => {
        input.value = solarRegions.custom.practicalSunHours.toFixed(1);
      });
    });
    summaryToggle?.addEventListener('click', () => {
      if (summaryShell?.classList.contains('is-at-section-end')) return;
      const willOpen = !root.classList.contains('is-summary-open');
      root.classList.toggle('is-summary-open', willOpen);
      syncSummaryToggle();
      if (!willOpen) {
        root.classList.remove('is-disclaimer-open');
        disclaimerToggle?.setAttribute('aria-expanded', 'false');
      }
      window.requestAnimationFrame(queueSummaryPosition);
    });

    const closeMobileSummary = () => {
      root.classList.remove('is-summary-open');
      root.classList.remove('is-disclaimer-open');
      syncSummaryToggle();
      disclaimerToggle?.setAttribute('aria-expanded', 'false');
      window.requestAnimationFrame(queueSummaryPosition);
    };

    results?.addEventListener('click', () => {
      if (!mobileResultsMedia.matches || summaryShell?.classList.contains('is-at-section-end')) return;
      closeMobileSummary();
    });

    disclaimerToggle?.addEventListener('click', () => {
      const willOpen = !root.classList.contains('is-disclaimer-open');
      root.classList.toggle('is-disclaimer-open', willOpen);
      disclaimerToggle.setAttribute('aria-expanded', String(willOpen));
      window.requestAnimationFrame(queueSummaryPosition);
    });

    labels.calculateSetup = 'Start Configurator (5 min)';
    labels.closeCalculator = { en: 'Hide Configurator', nl: 'Hide Configurator', de: 'Konfigurator ausblenden' }[locale];
    const introSteps = {
      en: [["Add your appliance(s)","Set their power and daily usage time."],["Result","Set your trip duration and compare suitable setups."],["Mix & Match","Choose your power station and optional solar panels."]],
      nl: [["Voeg je apparaten toe","Stel het vermogen en de dagelijkse gebruiksduur in."],["Resultaat","Stel je reisduur in en vergelijk passende setups."],["Mix & Match","Kies je powerstation en optionele zonnepanelen."]],
      de: [["Geräte hinzufügen","Stelle Leistung und tägliche Nutzungsdauer ein."],["Ergebnis","Stelle deine Reisedauer ein und vergleiche passende Setups."],["Mix & Match","Wähle deine Powerstation und optionale Solarmodule."]]
    }[locale];
    calculatorSection.querySelectorAll('[data-intro-step]').forEach(card => {
      const step = Number(card.dataset.introStep);
      const [title, description] = introSteps[step - 1];
      card.querySelector('.w21-power-calculator__intro-step-title').textContent = title;
      card.querySelector('.w21-power-calculator__intro-step-description').textContent = description;
      card.querySelector('[data-intro-step-label]').textContent = { en: 'Step', nl: 'Stap', de: 'Schritt' }[locale] + ' ' + step;
    });
    if (calculatorDisclosureLabel && calculatorContent?.hidden) calculatorDisclosureLabel.textContent = labels.calculateSetup;
    let disclosureTransitionFrame = 0;
    calculatorDisclosure?.addEventListener('click', () => {
      const willOpen = calculatorContent?.hidden !== false;
      if (!calculatorContent) return;

      // Click state changes are immediate; restore normal hover transitions
      // only after the new state has been resolved by the browser.
      window.cancelAnimationFrame(disclosureTransitionFrame);
      calculatorDisclosure.classList.add('is-changing-state');
      calculatorContent.hidden = !willOpen;
      calculatorSection.classList.toggle('is-expanded', willOpen);
      calculatorDisclosure.setAttribute('aria-expanded', String(willOpen));
      if (calculatorDisclosureLabel) {
        calculatorDisclosureLabel.textContent = willOpen ? labels.closeCalculator : labels.calculateSetup;
      }
      void window.getComputedStyle(calculatorDisclosure).color;
      disclosureTransitionFrame = window.requestAnimationFrame(() => {
        calculatorDisclosure.classList.remove('is-changing-state');
        disclosureTransitionFrame = 0;
      });

      if (willOpen) {
        // Inputs already keep the results current. Opening changes visibility,
        // not the setup, so preserve existing cards and focused input nodes.
        setStep(1, { render: false });
        window.requestAnimationFrame(() => {
          queueSummaryPosition();
          root.querySelector('[data-step-panel="1"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      } else {
        calculatorSection.classList.remove('is-summary-at-end');
        summaryShell?.classList.remove('is-visible', 'is-at-section-end');
        calculatorSection.querySelector('[data-calculator-intro]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });

    powerDisclosure?.addEventListener('click', () => {
      const willOpen = !root.classList.contains('is-power-picker-open');
      root.classList.toggle('is-power-picker-open', willOpen);
      powerDisclosure.setAttribute('aria-expanded', String(willOpen));
      queueSummaryPosition();
    });

    expansionDisclosure?.addEventListener('click', () => {
      if (state.selectedPowerStationId !== 'move_1800') return;
      selectionIsManual = true;
      state.expansionCount = 1;
      renderAll();
    });

    root.querySelectorAll('[data-sunlight-disclosure]').forEach(disclosure => disclosure.addEventListener('click', () => {
      const profile = disclosure.closest('[data-results-sunlight]');
      const container = profile || root;
      const className = profile ? 'is-profile-open' : 'is-sunlight-open';
      const willOpen = !container.classList.contains(className);
      container.classList.toggle(className, willOpen);
      disclosure.setAttribute('aria-expanded', String(willOpen));
      queueSummaryPosition();
    }));

    solarDisclosure?.addEventListener('click', () => {
      if (!solarOptions) return;
      if (mobileResultsMedia.matches) {
        const willOpen = !root.classList.contains('is-solar-picker-open');
        root.classList.toggle('is-solar-picker-open', willOpen);
        root.classList.remove('is-sunlight-open');
        sunlightDisclosure?.setAttribute('aria-expanded', 'false');
        solarOptions.hidden = !willOpen && !state.selectedSolarId;
        solarDisclosure.setAttribute('aria-expanded', String(willOpen));
        if (solarDisclosureLabel) solarDisclosureLabel.textContent = labels.addSolarPanels;
        queueSummaryPosition();
        return;
      }

      const willOpen = solarOptions.hidden;
      solarOptions.hidden = !willOpen;
      solarDisclosure.setAttribute('aria-expanded', String(willOpen));
      if (solarDisclosureLabel) solarDisclosureLabel.textContent = willOpen ? labels.hideSolarPanels : labels.addSolarPanels;
      queueSummaryPosition();
    });

    window.addEventListener('scroll', queueSummaryPosition, { passive: true });
    window.addEventListener('resize', queueSummaryPosition, { passive: true });
    window.addEventListener('resize', () => catalogueLayoutRefresh(), { passive: true });
    mobileResultsMedia.addEventListener?.('change', queueSummaryPosition);
    if ('ResizeObserver' in window && summaryPanel) {
      new ResizeObserver(queueSummaryPosition).observe(summaryPanel);
    }

    stepButtons.forEach((button) => button.addEventListener('click', () => setStep(Number(button.dataset.stepButton))));
    state.devices = cloneDevices(state.audienceId);
    syncSelectionSequence();
    setStep(1);
    queueSummaryPosition();
  };
  const initializeWithin = (container) => container.querySelectorAll('.w21-power-calculator__app[data-section-id]').forEach(initializeCalculator);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  else initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
